// /eventmapcreator — the club dashboard for event direction maps.
//
// A club signs in through a link emailed to its own inbox, fills in a logo and
// a contact email once, and from then on creates events: name, date and time,
// the room attendees are heading to, and a rich event page.
//
// Four rules shape the whole screen:
//
//  · ⚠️ THERE IS NO CLUB PASSWORD, ANYWHERE. Signing in is one field — the
//    club's email — and a one-use link that lands back on this page as
//    `?setup=…` (api/club-setup.js). The same form is how a club registers, how
//    it comes back in March, and how it recovers from an exec leaving with
//    everything: there is no second flow to find, nothing to reset, and nothing
//    for anybody at UMaps to hold. ⚠️ And there is no staff form here either:
//    the operator signs in at /admin and arrives on the shared session
//    (`initialToken`), so this page has ONE question on it and no password box
//    for a club to try.
//  · A club sees ONLY its own events. Enforced server-side in _eventStore.js —
//    the ownership check cannot live here, because here is the one place an
//    attacker isn't. (The central admin credential is the exception, and sees
//    every club's, since fixing an event means finding it first.)
//  · Logo + contact email come BEFORE anything else. An event goes on a public
//    map under a club's name; "who is this and how do we reach them" is the
//    minimum, so the dashboard opens on that form until it is answered rather
//    than letting a club get to the end of an event and be refused.
//  · Creating an event is THREE steps over a map, not one long form. What / when
//    / where, then the page, then the link — each step is a question somebody can
//    answer without holding the other two in their head, and the map behind is
//    the thing being decided about.
//
// The event page is authored in BlockEditor — text / image / video / link blocks
// with an animated [+] on every divider, the same interaction model as
// EngTourCreator's edit-stop editor, so somebody who has built a tour does not
// have to learn a second editor. It sits directly under a live banner showing
// the event's own name, logo, time and room, so the page is built inside the
// frame an attendee will read it in rather than in an abstract list of blocks
// that has to be previewed to be believed.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import BlockEditor, { withKeys, stripKeys } from './components/BlockEditor.jsx';
import { initialToken, clearToken } from './utils/adminSession.js';
import { qrBadgeSvg, qrBadgeSvgForDownload, MARK_CSS_WIDTH, UMAPS_BADGE_URL } from './utils/qrBadge.js';
import { eventUrl } from './utils/eventSlug.js';
import { shrinkLogoFile } from './utils/shrinkLogoFile.js';
import { guessPm, parseHm, toHm } from './utils/eventTime.js';
// ⚠️ `normalizeCustomTags` and its two caps are IMPORTED, never re-implemented
// here: this screen's Add button and counter must refuse exactly what the store
// refuses, or a club watches a tag it typed disappear on save with nothing said.
import {
  eventWhere, isOffCampus, offCampusLink, priceLabel,
  normalizeCustomTags, MAX_CUSTOM_TAGS, MAX_CUSTOM_TAG_CHARS,
} from './utils/eventTags.js';
import { EventTags, TicketButton, EVENT_TAGS_CSS } from './components/EventTags.jsx';
import { FACULTIES } from './utils/faculties.js';
import { createHttpDataSource, loadAllMapFiles } from './pathfinder/lib/dataSource.js';
import { liveRoomNames } from './utils/liveRoomNames.js';
import { buildMapData, attachRoomNames, attachBuildingInterior, searchRooms, searchBuildings,
  roomLabel, roomNumberBadge, getRoomLocation } from './pathfinder/lib/graph.js';
import UMapsWordmark from './components/UMapsWordmark.jsx';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;
const TOKEN_KEY = 'umaps-events-token';
// %20 — Safari's fetch() rejects a raw space (see PathfinderPage.js).
const DATA = '/Map%20Info/pathfinder/';

const CENTER = { lat: 49.80939016935401, lng: -97.1343494222327 };
const ROOM_ZOOM = 19;
// Close enough to read room outlines, wide enough to see a whole building.
const BUILDING_ZOOM = 18;
// Room numbers appear only once the rooms are big enough to hold them. Below
// this they overlap into a grey smear that hides the plan they are labelling.
// Deliberately the same level a building is framed at, so landing on a building
// and being able to read its rooms are the same moment — a plan you have to
// zoom in on before it says anything is a plan that failed to answer.
const ROOM_LABEL_ZOOM = BUILDING_ZOOM;

// The snapshot carries three buildings that are not buildings — the tunnel
// network, the outdoor graph, and the inter-building links. They are real
// entries in `mapData.buildings` because the router needs them, and offering
// "Outdoors" in a list of places to hold an event would be nonsense.
const PSEUDO_BUILDINGS = new Set(['tunnels', 'outdoors', 'building_connections', 'parking']);

const snapshotSource = createHttpDataSource(DATA);

const isEmail = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(s || '').trim());

// Where a club goes when it is stuck and we are the only ones who can unstick
// it — chiefly "we registered and are still not verified". One constant, so
// the address on the setup screen, the dashboard banner and the share screen
// cannot drift apart.
const CONTACT_EMAIL = 'umapscontact@gmail.com';

// The walkthrough on the dashboard. Stored as the bare video id, not a URL, so
// the embed and the "watch on YouTube" link beside it are built from one string
// and cannot end up pointing at two different videos.
const TUTORIAL_ID = 'CMerB9adtFM';


/** "2026-09-14" + "18:30" → a Date, or null. Local time, which is what a poster means. */
function eventDate(e) {
  if (!e?.date) return null;
  const d = new Date(`${e.date}T${e.time || '00:00'}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmtWhen = (e) => {
  const d = eventDate(e);
  if (!d) return 'No date set';
  return d.toLocaleString([], {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(e.time ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
};

const slugify = (s) => String(s || '').trim().replace(/[^A-Za-z0-9]+/g, '-')
  .replace(/-{2,}/g, '-').replace(/^-|-$/g, '').slice(0, 80);

/**
 * What UMaps has set this event to, told to the club that made it.
 *
 * ⚠️ A CLUB MUST BE ABLE TO SEE THIS. It is our decision about their event, and
 * the two states that are not `listed` are exactly the two that produce a
 * confused email — "our event isn't on the map" and "the QR on our poster does
 * nothing". Saying so on the card and on the share screen turns both of those
 * into something they can read for themselves. Mirrors `eventStatus` in
 * api/_eventStore.js, including the legacy `published` reading.
 */
const statusOf = (e) => (['listed', 'unlisted', 'disabled'].includes(e?.status)
  ? e.status
  : (e?.published === false ? 'disabled' : 'listed'));

const STATUS_NOTE = {
  unlisted: {
    tag: 'Unlisted',
    line: 'This event is not shown on the campus map or in the app. Your link and QR '
      + 'code still work, so anyone you send them to can open it.',
  },
  disabled: {
    tag: 'Turned off',
    line: 'This event has been turned off by UMaps: it is off the map and its link and '
      + 'QR code no longer open. Nothing you made has been deleted.',
  },
};

/**
 * Where an event stands with the UMaps moderators (Volunteer Tools), for the
 * club's own eyes. Null for an approved event — the ordinary case says nothing.
 *
 * ⚠️ EVERY NEW EVENT NOW WAITS FOR A MODERATOR before it is on the map or in
 * the app, and a club that cannot see that will read "saved" as "live" and
 * print the poster. A denied one carries the moderator's reason, verbatim, so
 * the card says what to change without the club having to find the email.
 * Mirrors `eventReview` in api/_eventStore.js: no field reads as approved.
 */
function reviewNoteOf(e) {
  const r = ['pending', 'approved', 'denied'].includes(e?.review) ? e.review : 'approved';
  if (r === 'pending') {
    return {
      tag: 'Waiting for review', tone: 'pending',
      line: e?.resubmittedAt
        ? 'Thanks for the changes — UMaps will look at this event again shortly. It goes on the '
          + 'map and in the app once it is approved.'
        : 'UMaps checks every new event before it goes on the campus map and in the app, usually '
          + 'within a day. You can keep editing it in the meantime.',
    };
  }
  if (r === 'denied') {
    return {
      tag: 'Needs changes', tone: 'denied',
      line: `UMaps couldn't approve this event yet: ${e?.denyReason || 'see our email'}${
        e?.denyChange ? ` What to change: ${e.denyChange}` : ''} Edit it and save to send it back for review.`,
    };
  }
  return null;
}

// The placeholders show the handle form, since that is the shortest thing that
// works; a pasted URL is always taken as given. LinkedIn asks for the full URL
// because a bare handle there is genuinely ambiguous — /company/ and /in/ are
// different things and guessing wrong sends people nowhere.
const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: '@umsu' },
  { key: 'discord', label: 'Discord', placeholder: 'invite code, or the full link' },
  { key: 'website', label: 'Website', placeholder: 'umsu.ca' },
  { key: 'facebook', label: 'Facebook', placeholder: '@umsu' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'full profile URL' },
];

const STEPS = [
  { n: 1, label: 'Event info' },
  { n: 2, label: 'Location' },
  { n: 3, label: 'Event page' },
  { n: 4, label: 'Share' },
];

/**
 * Which step dims the map behind it.
 *
 * Only the page editor, which needs the whole screen. Steps 1, 2 and 4 leave
 * the map readable — and step 2 IS the map, so dimming it there would hide the
 * thing being chosen. This also decides whether the scrim takes pointer events
 * at all; see the note on it in the wizard's markup.
 */
const dimStep = (n) => n === 3;

export default function EventCreatorPage() {
  // A club's own account first, the operator's session second.
  const [token, setToken] = useState(() => initialToken(TOKEN_KEY));
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const [club, setClub] = useState('');
  const [profile, setProfile] = useState(null);
  const [ready, setReady] = useState(false);
  const [events, setEvents] = useState([]);
  // How many people found each event, keyed by event id — see `ReachBox`. Rides
  // on the same `?mine=1` read as the events themselves, and is scoped
  // server-side to this club's own.
  const [stats, setStats] = useState({});

  const [view, setView] = useState('list');     // 'list' | 'profile' | 'wizard'
  const [step, setStep] = useState(1);
  const [searching, setSearching] = useState(false);   // the room list is open
  // The operator signed in here sees every club's events (api/events.js), which
  // is how one gets fixed without waiting on the club. It is also why the
  // dashboard's "approved" flag is forced true for them — they are the approval.
  // Whether this club's published events actually reach the public map. A club
  // can do everything else — profile, events, sharing — while this is false.
  const [approved, setApproved] = useState(true);
  // The operator, who sees every club's events here. The one thing they get
  // that a club does not is a Host field — see StepInfo.
  const [isAdmin, setIsAdmin] = useState(false);
  // Every club, for the operator's Host picker (the `mine=1` read carries it
  // for the operator only). Empty for a club.
  const [clubDir, setClubDir] = useState([]);
  const [draft, setDraft] = useState(null);     // the event being created / edited

  // The campus room index, for the location search. Loaded once, lazily — it is
  // the same snapshot the pathfinder reads, so a club picks from exactly what
  // the map can route to and never invents a room that cannot be found.
  const [mapData, setMapData] = useState(null);

  const saveToken = useCallback((t) => {
    setToken(t);
    if (!t) { clearToken(TOKEN_KEY); return; }
    try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* private mode */ }
  }, []);

  const api = useCallback(async (qs = '', body = null) => {
    const r = await fetch(`${API_BASE}/api/events${qs}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    // ⚠️ Says what getting back in COSTS. There is no password to retype: the
    // club has to ask for a link and wait for an email, so "sign in again"
    // would read as a five-second errand and send somebody looking for a login
    // form that does not exist.
    if (r.status === 401) {
      saveToken('');
      throw new Error('Your session has ended — ask for a new sign-in link to carry on.');
    }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
    return j;
  }, [token, saveToken]);

  /**
   * Arriving on a sign-in link — the other half of the login screen.
   *
   * A club asks for a link with its email and follows it back HERE, to
   * `?setup=<token>`: the page it was going to end up on anyway, so the email
   * carries one URL and there is no second route to get wrong.
   *
   * ⚠️ THE LINK IS NOT SPENT BY BEING OPENED. The page `check`s it (which reads
   * without consuming) and shows a button; spending it is a POST the club makes
   * by pressing that button. Mail scanners and link previewers fetch every URL
   * in an arriving email, so a link that signed you in on load would routinely
   * be burned by the appliance in front of the club's own inbox — and the club
   * would be told its brand-new link had already been used.
   *
   * `setupState` is what the page knows about the link, and each value is a
   * different screen: null while it is being checked, an object once it is
   * good, and `'dead'` for every way of being bad — expired, spent, mistyped —
   * because the server deliberately does not distinguish those and neither
   * should the page.
   */
  const [setupToken, setSetupToken] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('setup') || ''; }
    catch { return ''; }
  });
  const [setupState, setSetupState] = useState(null);   // null | {…} | 'dead'
  // The club's own name, asked for here rather than at registration — see the
  // note on the sign-up form. Only shown when the profile has none: a
  // pre-verified club arrives with its real name already filled in.
  const [setupName, setSetupName] = useState('');

  const setupApi = useCallback(async (body) => {
    const r = await fetch(`${API_BASE}/api/club-setup`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'That did not go through.');
    return j;
  }, []);

  useEffect(() => {
    if (!setupToken) return;
    let dead = false;
    setupApi({ op: 'check', token: setupToken })
      .then(j => { if (!dead) setSetupState(j); })
      .catch(() => { if (!dead) setSetupState('dead'); });
    return () => { dead = true; };
  }, [setupToken, setupApi]);

  const submitSetup = async (e) => {
    e?.preventDefault();
    setErr(''); setBusy(true);
    try {
      const j = await setupApi({
        op: 'signin', token: setupToken,
        ...(setupName.trim() ? { displayName: setupName.trim() } : {}),
      });
      // The link is dropped from BOTH the address bar and this component's
      // state: it has just been spent, so a copy left in either would turn the
      // next sign-out — or a refresh — into "that link has expired", which is
      // true and is not what somebody who just signed in needs to read.
      try { window.history.replaceState({}, '', window.location.pathname); } catch { /* fine */ }
      setSetupToken(''); setSetupState(null);
      saveToken(j.token);
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  };

  // ⚠️ THERE IS NO SIGN-IN FORM ON THIS PAGE AT ALL — not for a club, which has
  // no password, and not for the operator, who signs in at /admin and arrives
  // here on the shared session (`initialToken` reads the admin token, and
  // Events Admin links straight through). So this screen asks exactly one
  // question of exactly one kind of person, and a password box that clubs would
  // try to use does not exist to be tried.
  /**
   * Asking for a sign-in link — the way onto this tool, and the way back onto
   * it every time after that.
   *
   * ONE field: the inbox. It is the only thing that has to be true at this
   * moment and the only thing we can check — a name typed by a stranger proves
   * nothing. A club names itself after the link is spent, and everything else
   * (logo, socials, room) comes once it is in, where an empty field is a to-do
   * rather than a barrier between a club and its events.
   *
   * ⚠️ REGISTERING AND SIGNING IN ARE THE SAME BUTTON, which is not a shortcut
   * — it is the reason the form can be one field. The endpoint answers
   * identically whether the address is new, known, or half-registered (see the
   * note atop api/club-register.js), so the page shows one confirmation and
   * never branches on what happened; branching would be the leak. It is also
   * why "we've lost everything" needs no separate screen: that is this screen.
   */
  const [regEmail, setRegEmail] = useState('');
  const [regDone, setRegDone] = useState('');
  const register = async (e) => {
    e?.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/club-register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: regEmail.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error || 'That did not go through.'); setBusy(false); return; }
      setRegDone(regEmail.trim());
    } catch { setErr('Network error.'); }
    setBusy(false);
  };

  const reload = useCallback(async () => {
    const d = await api('?mine=1');
    setClub(d.club || '');
    setProfile(d.profile || null);
    setReady(!!d.ready);
    setApproved(d.approved !== false);
    setIsAdmin(!!d.admin);
    setClubDir(d.clubs || []);
    setEvents(d.events || []);
    setStats(d.stats || {});
    // A club that has not introduced itself yet lands on the profile form
    // rather than on an empty event list it is not allowed to add to.
    if (!d.ready) setView('profile');
    return d;
  }, [api]);

  useEffect(() => {
    if (!token) { setEvents([]); setStats({}); setClub(''); return; }
    let dead = false;
    reload().catch(e => { if (!dead) setErr(e.message); });
    return () => { dead = true; };
  }, [token, reload]);

  // Room index. Every building's nav file, which is what the pathfinder itself
  // loads on every visit — so this is a known-affordable cost, and it buys the
  // same search students get rather than a second, weaker one.
  useEffect(() => {
    if (!token || mapData) return;
    let dead = false;
    Promise.all([
      loadAllMapFiles(snapshotSource),
      liveRoomNames(snapshotSource, API_BASE)().catch(() => null), // sheets + the published overlay
    ])
      .then(([files, roomNames]) => {
        if (dead) return;
        const md = buildMapData(files);
        if (roomNames) attachRoomNames(md, roomNames);   // friendly names, e.g. "SSA Lounge"
        setMapData(md);
      })
      .catch(() => { /* the search just stays empty; everything else works */ });
    return () => { dead = true; };
  }, [token, mapData]);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const withDate = [...events].sort((a, b) => (eventDate(a)?.getTime() || 0) - (eventDate(b)?.getTime() || 0));
    return {
      upcoming: withDate.filter(e => (eventDate(e)?.getTime() ?? Infinity) >= now),
      // Newest-first among the finished ones: the one that just happened is the
      // one somebody is most likely looking for.
      past: withDate.filter(e => (eventDate(e)?.getTime() ?? Infinity) < now).reverse(),
    };
  }, [events]);

  // ── Profile ────────────────────────────────────────────────────────────────
  const [pDraft, setPDraft] = useState({ displayName: '', shortName: '', email: '', logo: '', room: null, socials: {} });
  useEffect(() => {
    if (profile) setPDraft({
      displayName: profile.displayName || '', shortName: profile.shortName || '',
      email: profile.email || '',
      logo: profile.logo || '', room: profile.room || null,
      socials: { ...(profile.socials || {}) },
    });
  }, [profile]);
  const setSocial = (k, v) => setPDraft(p => ({ ...p, socials: { ...p.socials, [k]: v } }));

  const fileRef = useRef(null);

  const readDataUrl = (file) => new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = () => rej(new Error('Could not read that file.'));
    fr.readAsDataURL(file);
  });

  // The logo is scaled down in the browser first (utils/shrinkLogoFile.js), so a
  // phone photo over the 2 MB upload limit still goes through; the server then
  // shrinks it to the 256 px every logo is stored at, whatever arrives.
  // `kind: 'club-logo'` is what tells the endpoint this is a logo and not an
  // event-body image, which is stored full size.
  const pickLogo = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setErr('That image is over 20 MB — please use a smaller one.'); return; }
    setErr(''); setStatus('Uploading logo…');
    try {
      const dataUrl = await shrinkLogoFile(file);
      // A data URL is ~4/3 the bytes it carries; the server refuses past 2 MB.
      if (dataUrl.length * 0.75 > 2 * 1024 * 1024) {
        setErr('That logo is over 2 MB — please use a smaller image.'); setStatus(''); return;
      }
      const d = await api('', { logo: dataUrl, kind: 'club-logo' });
      setPDraft(p => ({ ...p, logo: d.url }));
      setStatus('Logo uploaded — save to keep it.');
    } catch (e) { setErr(e.message); setStatus(''); }
  };

  const saveProfile = async () => {
    setBusy(true); setErr(''); setStatus('Saving…');
    try {
      const d = await api('', { profile: pDraft });
      setProfile(d.profile); setReady(!!d.ready);
      setStatus(d.ready ? 'Saved.' : 'Saved — a logo and a valid contact email are still needed.');
      if (d.ready) setView('list');
    } catch (e) { setErr(e.message); setStatus(''); }
    setBusy(false);
  };

  // ── Events ─────────────────────────────────────────────────────────────────
  // `host` is not a field any more. An event is always by the club that is
  // signed in, so asking was offering somebody the chance to publish under
  // another name by accident — or on purpose.
  const hostName = profile?.displayName || club;

  // ⚠️ WHO AN EVENT IS BY, as the public read works it out (`publicHost` in
  // api/events.js: the stored host, else the OWNING club's name). The operator
  // sees every club's events here, and an event put under a club from the Host
  // picker has a blank host — so falling back to `hostName`, the operator's own
  // name, would build that event's link and QR under "UMaps" and print a code
  // that opens nothing.
  const dirByName = useMemo(() => Object.fromEntries(clubDir.map(c => [c.username, c])), [clubDir]);
  const hostOf = useCallback((e) => e?.host
    || (isAdmin && e?.club && e.club !== club ? dirByName[e.club]?.displayName || e.club : hostName),
  [isAdmin, club, dirByName, hostName]);
  // The operator makes events for one club after another; the last club picked
  // is the first guess for the next new one. Never saved anywhere — a reload
  // starts with no guess, so nothing is filed under a club nobody chose today.
  const lastOwner = useRef('');

  const newEvent = () => ({
    id: '', name: '', slug: '', host: isAdmin ? '' : hostName,
    // The operator says whose event this is in the Host picker (StepInfo).
    owner: isAdmin ? lastOwner.current : '',
    date: '', time: '', endTime: '',
    buildingSlug: '', buildingName: '', room: null, blocks: [], status: 'listed',
    // The tags, at their "nothing said" values. Spelled out rather than left
    // undefined so the form is never uncontrolled on the first keystroke, and
    // so a save from a fresh draft clears whatever a previous one set.
    freeFood: false, faculties: [], tickets: null, offCampus: null, customTags: [],
  });

  const openWizard = (e, at = 1) => {
    // ⚠️ AN EXISTING EVENT KEEPS ITS OWN HOST when the operator opens it. This
    // line used to stamp the signed-in account's name over it unconditionally,
    // so editing a club's event as UMaps rewrote the host to "UMaps" in the
    // draft and the save published it under our name — the event belonged to
    // the club, said so in the store, and still came out wrong.
    //
    // A CLUB keeps the old refresh: it can only ever be itself, has no field to
    // correct this with, and a club that renamed itself should see the new name
    // carried onto its events rather than the one stored last year.
    setDraft(e
      ? { ...e, owner: e.club, host: isAdmin ? (e.host || '') : hostName, blocks: withKeys(e.blocks) }
      : newEvent());
    setStep(at); setView('wizard'); setErr(''); setStatus('');
  };

  const closeWizard = () => { setView('list'); setDraft(null); setStep(1); };
  // Where a press on the scrim started, so a drag can be told from a tap. See
  // the note on the scrim in the wizard's markup.
  const scrimDown = useRef(null);
  // How much of the stage the wizard card is covering, so the map can frame a
  // building into the part that is left. Measured rather than assumed: the card
  // is capped as a fraction of the stage but is only as tall as its content.
  const wizRef = useRef(null);
  const [cardH, setCardH] = useState(0);
  useEffect(() => {
    const el = wizRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setCardH(el.offsetHeight || 0));
    ro.observe(el);
    setCardH(el.offsetHeight || 0);
    return () => ro.disconnect();
  }, [view, step]);

  /** Save the draft and return the saved event, or throw. */
  const saveEvent = useCallback(async () => {
    // The operator names the OWNER (a club's login, or their own for a UMaps
    // event) and the event is stored under that club, host blank — the club's
    // own name. A typed host survives only on a UMaps-owned event that already
    // had one (from before the picker). A club sends neither: it is always
    // itself, and the server ignores `owner` from anybody but the operator.
    const owner = isAdmin ? (draft.owner || club) : '';
    const { owner: _drop, ...rest } = draft;
    const body = {
      ...rest,
      ...(isAdmin ? { owner, host: owner === club ? (draft.host || '') : '' } : { host: hostName }),
      slug: draft.slug || slugify(draft.name),
      blocks: stripKeys(draft.blocks),
    };
    const d = await api('', { event: body });
    if (isAdmin) lastOwner.current = d.event.club;
    await reload();
    // Keep the editor's keys: the saved copy comes back without them, and
    // re-keying every block on save would remount each one and throw away the
    // caret of whatever was being typed.
    setDraft({ ...d.event, owner: d.event.club, blocks: withKeys(d.event.blocks) });
    return d.event;
  }, [draft, hostName, isAdmin, club, api, reload]);

  const saveAndAdvance = async () => {
    if (!draft?.name.trim()) { setErr('Give the event a name.'); return; }
    if (isAdmin && !draft.owner) { setErr('Pick who is hosting this event — a club, or UMaps.'); return; }
    setBusy(true); setErr(''); setStatus('Saving…');
    try {
      const saved = await saveEvent();
      setStatus(`Saved “${saved.name}”.`);
      setStep(4);
    } catch (e) { setErr(e.message); setStatus(''); }
    setBusy(false);
  };

  /** Upload one body image through the same endpoint the logo uses. */
  const uploadBlockImage = useCallback(async (file) => {
    if (file.size > 2 * 1024 * 1024) throw new Error('That image is over 2 MB — please use a smaller one.');
    const d = await api('', { logo: await readDataUrl(file) });
    return d.url;
  }, [api]);

  const removeEvent = async (e) => {
    if (!window.confirm(`Delete “${e.name || 'this event'}”? Any link or QR already shared for it stops working.`)) return;
    setBusy(true); setErr('');
    try {
      await api('', { deleteId: e.id });
      setStatus('Event deleted.');
      await reload();
      closeWizard();
    } catch (er) { setErr(er.message); }
    setBusy(false);
  };

  // The link a club hands out: /events?e=UMSU-Welcome-Day. Derived from the club
  // and the event name (utils/eventSlug.js) rather than the authored `slug`, so
  // it names what it opens and cannot fall out of step with the event it belongs
  // to. Anything shared under an older slug still resolves.
  // The club the operator has picked for the open draft, or null for a UMaps
  // event (and always null for a club, which is only ever itself).
  const draftClub = isAdmin && draft?.owner && draft.owner !== club ? dirByName[draft.owner] || null : null;
  // On the operator's dashboard a card wears the logo of the club it is BY.
  const cardProfile = (e) => (isAdmin && e?.club !== club && dirByName[e?.club]
    ? { logo: dirByName[e.club].logo } : profile);
  const hosted = useMemo(() => events.map(o => ({ ...o, host: hostOf(o) })), [events, hostOf]);
  const shareLink = useCallback(
    (e) => eventUrl({ ...e, host: hostOf(e) }, undefined, hosted),
    [hostOf, hosted]);

  /** The event QR, as a downloadable SVG. Vector, because a club will put it on
   *  a poster and a scaled-up PNG prints fuzzy.
   *
   *  ⚠️ The mark is EMBEDDED here rather than linked. This file leaves the site
   *  — it gets opened in Illustrator, emailed to a print shop, dropped into
   *  Canva — and a `/UMaps%20Logo…` reference resolves to nothing in all three,
   *  printing a poster with a hole in the middle of its QR. */
  const downloadQr = async (e) => {
    const svg = await qrBadgeSvgForDownload(shareLink(e), 1000);
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(e.name) || 'event'}-qr.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Where the map behind the wizard should be looking: the chosen room. Found by
  // building + room number rather than by carrying an index key on the saved
  // record — the server re-derives every field it stores, so an extra key would
  // survive in memory and vanish on reload, which is the kind of difference that
  // works right up until somebody reopens an event.
  // The room's own outline, once that building's interior has been fetched.
  // `loadAllMapFiles` deliberately loads footprints only — a building's insides
  // are ~40× the bytes — so the shape arrives on demand, and until it does the
  // map shows the door node, which is still the right place.
  // Which building the location step is looking at, and whether a tap on the
  // map drops a pin. Both live up here because the MAP is up here — the step is
  // a card floating over it, and the two have to agree about what is shown.
  const [pickedBuilding, setPickedBuilding] = useState(null);
  // Which floor is being looked at. A building is a stack of plans, and every
  // one of them is a place an event can be — so this decides which rooms the map
  // draws, which rooms the search offers, and which floor a dropped pin belongs
  // to. It lives up here for the same reason the building does: the map is up
  // here, and the two have to agree about what is on screen.
  const [pickedFloor, setPickedFloor] = useState(1);
  const [pinning, setPinning] = useState(false);
  // Reopening the wizard on an existing event should start on that event's
  // building rather than on whatever was last looked at — and on that event's
  // FLOOR, since showing floor 1 for a room on floor 3 would draw a plan the
  // chosen room is not in.
  useEffect(() => {
    if (view !== 'wizard') { setPinning(false); setPickedBuilding(null); return; }
    const slug = draft?.buildingSlug || null;
    setPickedBuilding(slug);
    setPickedFloor(roomFloor(mapData, draft?.room) ?? groundFloor(buildingFloors(mapData, slug)));
    setPinning(false);
    // Only when the wizard opens or the event changes — not on every draft edit,
    // which would fight the user's own building or floor choice. `mapData` is
    // here because a room's floor cannot be looked up before it arrives, and it
    // lands once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, draft?.id, mapData]);

  // Ticking "off campus" empties the location, and the map behind the card has
  // to empty with it — otherwise the last building's floor plan stays drawn
  // under a form asking for a street address, which reads as the building still
  // being part of the answer. Separate from the effect above because this fires
  // on a draft edit, which that one deliberately ignores.
  useEffect(() => {
    if (isOffCampus(draft)) { setPickedBuilding(null); setPinning(false); }
  }, [draft]);

  /**
   * A tap on the map while pinning: turn a lat/lng into somewhere the router
   * can actually walk to.
   *
   * The point itself is what gets drawn, but a point is not a destination —
   * the route network is nodes — so it snaps to the nearest node in the
   * building and stores BOTH. `nodeIds` is what routing uses; `point` is what
   * the map draws and what makes the pin land where the club put it rather
   * than on the node it borrowed.
   *
   * ⚠️ The FLOOR is the third thing, and it is not decoration. A lat/lng in a
   * six-storey building names six places, so the pin is stored with the floor
   * being looked at — which is also the floor the snap prefers, so the node it
   * borrows is on the same plan the club was pointing at rather than the
   * stairwell two storeys below.
   */
  const dropPin = useCallback((point) => {
    const slug = pickedBuilding || draft?.buildingSlug;
    if (!mapData || !slug) return;
    const b = mapData.buildings.find(x => x.slug === slug);
    const floors = buildingFloors(mapData, slug);
    const floor = floors.includes(pickedFloor) ? pickedFloor : groundFloor(floors);
    const node = nearestNodeTo(mapData, slug, point, floor);
    if (!node) {
      setErr('That building has no route data yet, so a pin there could not be walked to.');
      return;
    }
    setDraft(d => ({
      ...d,
      buildingSlug: slug,
      buildingName: d.buildingName || b?.name || '',
      room: {
        building: slug,
        buildingName: d.buildingName || b?.name || '',
        roomNumber: '',
        roomName: '',
        label: 'Pinned spot',
        nodeIds: [node.id],
        point,
        floor,
      },
    }));
    setPinning(false);
    setStatus(`Spot placed on ${floorWord(floor).toLowerCase()}. Give it a name if you like.`);
  }, [mapData, pickedBuilding, pickedFloor, draft?.buildingSlug]);

  const [interiors, setInteriors] = useState(0);
  const interiorReq = useRef(new Set());
  useEffect(() => {
    // The building being LOOKED at on the location step, or the saved one.
    const slug = pickedBuilding || draft?.room?.building;
    if (!mapData || !slug) return;
    const b = mapData.buildings.find(x => x.slug === slug);
    // ⚠️ GUARD ON `outlineOnly`, NOT on whether there are any polygons.
    //
    // `loadAllMapFiles` ships a footprint STUB for every building — one outline
    // per floor — so `polygonsByFloor.size` is already non-zero before any
    // interior has been fetched. The old check therefore returned early every
    // time and the insides were never loaded at all: a chosen room's outline
    // never highlighted (it silently fell back to the door node), and the
    // location step drew a building with no rooms in it. `outlineOnly` is the
    // flag that actually means "footprint only", and is what PathfinderTab
    // guards on for the same fetch.
    if (!b || !b.outlineOnly) return;
    if (interiorReq.current.has(slug)) return;   // one request per building
    interiorReq.current.add(slug);
    let dead = false;
    snapshotSource.loadBuilding(slug)
      .then(json => {
        if (dead || !json) return;
        attachBuildingInterior(mapData, slug, json);
        setInteriors(n => n + 1);   // mapData is mutated in place, so nudge React
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [mapData, pickedBuilding, draft?.room?.building]);

  /**
   * What the map should be showing: ONE FLOOR of one building, and whatever is
   * chosen on it.
   *
   * It used to be four cases in priority order, and a chosen room was the first
   * of them — which meant picking a room threw the whole plan away and left one
   * highlighted shape floating on satellite imagery, with nothing around it to
   * say which corridor or which end of the building it was. So the floor is now
   * the constant and the selection is a highlight on top of it: the same plan
   * before and after, with one shape filled in.
   *
   * Everything is filtered to `pickedFloor`, the footprint included. Nothing
   * belonging to another storey is drawn, so a marker for a pin or a room on a
   * floor that is not on screen is deliberately absent rather than hovering over
   * a plan it is not part of.
   */
  const focus = useMemo(() => {
    if (!mapData) return null;
    const room = draft?.room;
    const slug = pickedBuilding || room?.building;
    if (!slug) return null;
    const b = mapData.buildings.find(x => x.slug === slug);
    if (!b) return null;
    const floor = pickedFloor;

    // Which rooms a tap can actually choose: the ones the routing index knows a
    // door for. A room with no door is drawn like any other but is inert — it
    // cannot be routed to, so offering it would be offering a dead end.
    const known = new Set();
    for (const e of mapData.roomIndex || []) {
      if (e.building === slug && e.roomNumber) known.add(String(e.roomNumber).toLowerCase());
    }
    const base = {
      slug, floor,
      footprint: floorOutlines(mapData, slug, floor),
      rooms: buildingRooms(mapData, slug, floor).map(r => ({
        ...r,
        pickable: r.type === 'room' && !!r.roomNumber
          && known.has(String(r.roomNumber).toLowerCase()),
      })),
    };

    if (room?.building === slug && room.roomNumber) {
      const entry = (mapData.roomIndex || []).find(
        r => r.building === slug && String(r.roomNumber) === String(room.roomNumber));
      const loc = entry ? getRoomLocation(mapData, entry) : null;
      if (loc) return {
        ...base, kind: 'room', roomNumber: room.roomNumber,
        rings: loc.floor === floor ? loc.rings : [],
        position: loc.floor === floor ? loc.position : null,
      };
    }

    // A pin: a place with no room polygon to highlight, so the marker is the
    // whole answer, and the rooms behind it are what make "which corridor is
    // that" legible.
    if (room?.building === slug && room.point?.lat) {
      return {
        ...base, kind: 'pin', rings: [],
        position: (room.floor ?? 1) === floor ? room.point : null,
      };
    }

    return { ...base, kind: 'building', rings: [], position: null };
    // `interiors` is what re-runs this once the polygons have landed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData, draft, pickedBuilding, pickedFloor, interiors]);

  /**
   * Choosing a room by tapping it on the plan.
   *
   * The map is the thing being read on this step, so the room somebody means is
   * already under their finger — asking them to find its number and type it into
   * a box is asking them to describe what they are pointing at. It goes through
   * the same index entry the search box uses, so both ways of picking store the
   * same thing.
   */
  const pickRoomOnMap = useCallback((roomNumber) => {
    const slug = pickedBuilding || draft?.room?.building;
    if (!mapData || !slug) return;
    const entry = (mapData.roomIndex || []).find(
      r => r.building === slug && String(r.roomNumber).toLowerCase() === String(roomNumber).toLowerCase());
    if (!entry) return;
    setDraft(d => ({
      ...d,
      buildingSlug: slug,
      buildingName: d.buildingName || entry.buildingName || '',
      room: roomFromEntry(entry),
    }));
    setErr('');
  }, [mapData, pickedBuilding, draft?.room?.building]);

  // A chosen location drags the floor to itself: picking a room on floor 3 and
  // leaving the plan showing floor 1 would draw a building the room is not in.
  // Only when the location changes, so a club that then taps another floor tab
  // to look around keeps looking.
  useEffect(() => {
    const f = roomFloor(mapData, draft?.room);
    if (f != null) setPickedFloor(f);
  }, [mapData, draft?.room]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="ec-login">
        <style>{CSS}</style>

        {/* ── Arriving on a sign-in link: say whose it is, then straight in ── */}
        {setupToken ? (
          setupState === null ? (
            <div className="ec-card"><h1>Event Map Creator</h1><p>Checking your link…</p></div>
          ) : setupState === 'dead' ? (
            <div className="ec-card">
              <h1>That link has expired</h1>
              <p>
                A sign-in link works once, and only for a day. Ask for a new one — it
                costs nothing, and you can do it as often as you need.
              </p>
              {/* The way out is another link rather than "sign in instead", because
                  there is no other way in: a club whose link has died has nothing
                  else to try, and offering it a sign-in form would be offering a
                  password box it does not have a password for. */}
              <button type="button" className="ec-btn ec-primary"
                      onClick={() => { window.location.href = window.location.pathname; }}>
                Send me a new link
              </button>
            </div>
          ) : (
            <form className="ec-card" onSubmit={submitSetup}>
              <h1>{setupState.firstTime ? 'Welcome to UMaps' : 'Sign in'}</h1>
              {/* Named, so somebody who has been forwarded a link can see before
                  they press anything that it signs them in as another club. */}
              <p>
                You&rsquo;re signing in as{' '}
                <b>{setupState.displayName || setupState.email || 'your club'}</b>
                {setupState.displayName && setupState.email ? <> ({setupState.email})</> : null}.
              </p>
              {/* Asked for only when we do not already have it. A pre-verified
                  club carries its real name from our own list, and re-asking
                  would invite somebody to overwrite it with an abbreviation. */}
              {!setupState.displayName && (
                <label className="ec-field">Your club&rsquo;s name
                  <input className="ec-input" autoFocus value={setupName} maxLength={80}
                         placeholder="Science Students' Association"
                         onChange={e => setSetupName(e.target.value)}
                         autoComplete="organization" />
                </label>
              )}
              {/* ⚠️ A BUTTON, not an automatic sign-in on load. Spending the link
                  is this press — see the note on `setupToken` — so a mail scanner
                  that fetched the URL cannot have used it up before the club got
                  there. */}
              <button className="ec-btn ec-primary"
                      disabled={busy || (!setupState.displayName && setupName.trim().length < 2)}>
                {busy ? 'Signing in…' : setupState.firstTime ? 'Set up my club' : 'Sign in'}
              </button>
              {/* ⚠️ Said BEFORE they start building, not discovered on the share
                  screen with a link in hand. A club we already know publishes
                  immediately; anyone else builds now and appears once we have
                  looked at them, and has an address to chase it with. */}
              {setupState.firstTime && (
                setupState.approved ? (
                  <div className="ec-note">
                    Your club is already verified — anything you publish goes straight onto
                    the campus map.
                  </div>
                ) : (
                  <div className="ec-hold">
                    <b>Your email isn&rsquo;t verified yet.</b> You can set everything up and build
                    events right away, but your first events won&rsquo;t show on the public map
                    until we&rsquo;ve verified your club. If that hasn&rsquo;t happened in a few days,
                    email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                  </div>
                )
              )}
              {/* The mechanism, once, on the screen where it is being used —
                  so "what is my password" is answered before it is asked. */}
              <p className="ec-hint">
                There&rsquo;s no password to remember. Every time you come back, ask for a
                link from this page and it arrives at your club&rsquo;s email.
              </p>
              {err && <div className="ec-err">{err}</div>}
            </form>
          )
        ) : (
          /* ── The link is in their inbox ── */
          regDone ? (
            <div className="ec-card">
              <h1>Check your email</h1>
              <p>
                If <b>{regDone}</b> is a club address we can use, a sign-in link is on its
                way. It can take a minute — check spam if it hasn&rsquo;t arrived.
              </p>
              {/* Deliberately not "we've sent your link" or "we created your
                  account". The endpoint gives the same answer for an address
                  that already has an account and for one that does not, so that
                  typing a club's address cannot be used to find out whether it
                  is registered. Promising anything specific here would undo
                  that. */}
              <button type="button" className="ec-btn ec-quiet"
                      onClick={() => { setRegDone(''); setRegEmail(''); }}>
                Use a different email
              </button>
            </div>
          ) : (
            /* ── The way in, and the only one: one field, one button ── */
            <form className="ec-card" onSubmit={register}>
              <h1>Event Map Creator</h1>
              <p>
                Put your club&rsquo;s events on the campus map. Enter your club&rsquo;s email and
                we&rsquo;ll send you a link to sign in.
              </p>
              <input className="ec-input" autoFocus type="email" placeholder="Club email"
                     value={regEmail} onChange={e => setRegEmail(e.target.value)}
                     autoComplete="email" />
              <button className="ec-btn ec-primary" disabled={busy || !isEmail(regEmail)}>
                {busy ? 'Sending…' : 'Email me a sign-in link'}
              </button>
              {/* Said before the button, not after: this is the only moment
                  anybody is looking at which address a club signs itself up on,
                  and one registered to an exec's personal account leaves with
                  that exec. */}
              <p className="ec-hint">
                Use your club&rsquo;s <b>main email</b> — the one that stays with the club when
                execs change, not a personal address.
              </p>
              {err && <div className="ec-err">{err}</div>}
            </form>
          )
        )}
      </div>
    );
  }

  return (
    <div className="ec-root">
      <style>{CSS}</style>
      <header className="ec-top">
        <UMapsWordmark tool="Event Map Creator" />
        {profile?.logo && <img className="ec-toplogo" src={profile.logo} alt="" />}
        <span className="ec-who">{hostName}</span>
        <nav className="ec-nav">
          <button className={`ec-tab ${view === 'list' ? 'on' : ''}`}
                  onClick={closeWizard}>Events</button>
          <button className={`ec-tab ${view === 'profile' ? 'on' : ''}`}
                  onClick={() => setView('profile')}>Club details</button>
        </nav>
        {/* Confirmed, which a sign-out button rarely deserves — but this one
            costs an email round-trip to undo, and on a shared office computer
            it is next to the tabs somebody presses without reading. */}
        <button className="ec-btn"
                onClick={() => {
                  if (window.confirm('Sign out? There\'s no password — you\'ll need a new sign-in link emailed to your club to get back in.')) saveToken('');
                }}>Sign out</button>
        {(err || status) && <span className={`ec-status ${err ? 'err' : ''}`}>{err || status}</span>}
      </header>

      {/* ── Club details ── */}
      {view === 'profile' && (
        <main className="ec-main ec-narrow">
          {!ready && (
            <div className="ec-gate">
              Before your first event, tell us who you are. Your logo appears on the event
              map so attendees recognise it, and the contact email is how we reach you if
              something about the event needs fixing — it is never shown publicly.
            </div>
          )}
          <div className="ec-row">
            <label className="ec-field">Club name, as attendees should see it
              <input className="ec-input" value={pDraft.displayName}
                     placeholder="Science Students' Association"
                     onChange={e => setPDraft(p => ({ ...p, displayName: e.target.value }))} />
            </label>
            {/* Beside the full name rather than under it, because the pair is
                the point: what it is called, and what it is called when there
                is no room. Optional — plenty of clubs have no initialism, and
                inventing one for them is worse than letting the long name
                wrap. */}
            <label className="ec-field ec-short">Short name <span className="ec-opt">optional</span>
              <input className="ec-input" value={pDraft.shortName} placeholder="SSA" maxLength={24}
                     onChange={e => setPDraft(p => ({ ...p, shortName: e.target.value }))} />
            </label>
          </div>
          <span className="ec-hint">
            The short name is used where the full one will not fit — a map pin, a crowded
            list. Leave it empty and the full name is used everywhere.
          </span>
          <label className="ec-field">Contact email
            <input className="ec-input" type="email" value={pDraft.email} placeholder="events@umsu.ca"
                   onChange={e => setPDraft(p => ({ ...p, email: e.target.value }))} />
            {pDraft.email && !isEmail(pDraft.email) && <span className="ec-hint err">That does not look like an email address.</span>}
          </label>
          <div className="ec-field">Logo
            <div className="ec-logorow">
              {pDraft.logo
                ? <img className="ec-logo" src={pDraft.logo} alt="" />
                : <div className="ec-logo ec-logo-empty">No logo</div>}
              <div>
                <button className="ec-btn" onClick={() => fileRef.current?.click()}>
                  {pDraft.logo ? 'Replace logo' : 'Upload logo'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" hidden
                       onChange={e => { pickLogo(e.target.files?.[0]); e.target.value = ''; }} />
                <div className="ec-hint">PNG, JPG, WEBP or GIF · a square logo looks best as a map pin.</div>
              </div>
            </div>
          </div>
          {/* The club's own room — where students find the club between events.
              Same picker as an event's location, so a club can only name a room
              the map can actually walk somebody to, and so the one search on
              this tool works the same way in both places it appears.

              Optional: plenty of clubs have no room, and one that has none must
              still be able to put an event on the map. */}
          <div className="ec-field">Club room <span className="ec-opt">optional</span>
            <RoomPicker value={pDraft.room} mapData={mapData}
                        onChange={room => setPDraft(p => ({ ...p, room }))} />
            <span className="ec-hint">
              Shown to students with your socials when they tap your club's name, with directions
              to it. Search by room number — “204” or “EITC 204”.
            </span>
          </div>

          {/* Optional, and last: a club with no socials must still be able to
              put an event on the map. Each accepts a full URL, a bare domain or
              just a handle — people paste whatever is in front of them, and
              refusing two of those three is how a field silently does nothing. */}
          <div className="ec-field">Socials <span className="ec-opt">optional</span>
            <div className="ec-socials">
              {SOCIAL_FIELDS.map(f => (
                <label key={f.key} className="ec-social">
                  <span>{f.label}</span>
                  <input className="ec-input" value={pDraft.socials?.[f.key] || ''}
                         placeholder={f.placeholder}
                         onChange={e => setSocial(f.key, e.target.value)} />
                </label>
              ))}
            </div>
            <span className="ec-hint">
              Shown to attendees when they tap your club's name on an event.
            </span>
          </div>

          <button className="ec-btn ec-primary" onClick={saveProfile}
                  disabled={busy || !pDraft.logo || !isEmail(pDraft.email)}>
            {busy ? 'Saving…' : 'Save club details'}
          </button>
        </main>
      )}

      {/* ── Event list ── */}
      {view === 'list' && (
        <main className="ec-main">
          <div className="ec-listhead">
            <h2>Your events</h2>
            <button className="ec-btn ec-primary" disabled={!ready} onClick={() => openWizard(null)}>
              + New event
            </button>
          </div>
          {!ready && (
            <div className="ec-gate">
              Add your logo and a contact email in <button className="ec-link" onClick={() => setView('profile')}>Club details</button> before creating an event.
            </div>
          )}
          {/* ⚠️ Said on the screen where the question gets asked. A club that
              has published an event and cannot find it on the map has no way to
              tell whether it is waiting on us or has done something wrong — and
              the alternative to this sentence is an email somebody answers by
              hand. It is amber rather than red: nothing is broken and nothing
              they made is lost, they are in a queue. */}
          {ready && !approved && (
            <div className="ec-hold">
              <b>Your club isn't verified yet.</b> Everything here works — build and publish
              events as normal — but they won't show on the public campus map until we've
              verified you. We'll email you when that happens. If it has been a few days,
              email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </div>
          )}

          {/* The tutorial. Open by default for a club with nothing yet — the
              only visit where "what do I do here" is the actual question — and
              folded away once they have made something, because from then on
              this screen is a list they came back to check.
              ⚠️ Gated on `club`, which arrives with the events: `defaultOpen`
              is read once, when the component mounts, and `events` is an empty
              array until the fetch lands — so rendered any earlier, the video
              unfolded itself on every visit for every club. */}
          {club && <TutorialVideo defaultOpen={events.length === 0} />}

          <h3 className="ec-sec">Upcoming ({upcoming.length})</h3>
          {upcoming.length === 0 && <div className="ec-empty">Nothing coming up yet.</div>}
          <div className="ec-grid">
            {upcoming.map(e => (
              <EventCard key={e.id} e={e} profile={cardProfile(e)} by={isAdmin ? hostOf(e) : ''} link={shareLink(e)}
                         reach={stats[e.id]}
                         onOpen={() => openWizard(e)} onShare={() => openWizard(e, 4)}
                         onDelete={() => removeEvent(e)} />
            ))}
          </div>

          {past.length > 0 && (
            <>
              <h3 className="ec-sec">Past ({past.length})</h3>
              <div className="ec-grid ec-past">
                {past.map(e => (
                  <EventCard key={e.id} e={e} profile={cardProfile(e)} by={isAdmin ? hostOf(e) : ''} link={shareLink(e)}
                             reach={stats[e.id]}
                             onOpen={() => openWizard(e)} onShare={() => openWizard(e, 4)}
                             onDelete={() => removeEvent(e)} />
                ))}
              </div>
            </>
          )}
        </main>
      )}

      {/* ── The three-step wizard, over the campus map ── */}
      {view === 'wizard' && draft && (
        <div className="ec-stage">
          <CampusMap focus={focus} pinning={pinning} onPin={dropPin}
                     onRoom={step === 2 ? pickRoomOnMap : null} padTop={cardH} />
          {/* ⚠️ THE SCRIM MUST NOT EAT THE MAP.
              It covers the whole stage, so while it was clickable at every step
              two things went wrong at once: the map underneath could not be
              dragged at all (the scrim swallowed the gesture), and the drag
              ended in a click that ran `closeWizard` — so a club trying to move
              the map lost the event it was building. That is the "it crashes
              when I drag the map" report.

              So it is inert unless it is actually dimming something, and the
              dimmed one closes on a CLICK and not on the end of a drag. */}
          <div className={`ec-scrim ${dimStep(step) ? 'dim' : ''}`}
               onPointerDown={e => { scrimDown.current = { x: e.clientX, y: e.clientY }; }}
               onClick={e => {
                 const d = scrimDown.current;
                 scrimDown.current = null;
                 // A few pixels of travel is a finger on a phone holding still,
                 // not a drag. Anything past that was a gesture aimed at the
                 // map, and closing on it would be the same bug in a smaller
                 // form.
                 if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6) return;
                 closeWizard();
               }} />
          {/* Step 1 is a compact card at the top: what it asks about is WHERE the
              event is, and a dialog filling the screen hides the map that
              answers it. Step 2 takes the full height because the page editor
              needs it, and step 3 is short again. */}
          <section ref={wizRef}
                   className={`ec-wiz ${dimStep(step) ? 'tall' : 'compact'} ${searching ? 'searching' : ''}`}
                   role="dialog" aria-label={draft.id ? 'Edit event' : 'New event'}>
            <div className="ec-wizhead">
              <button className="ec-wizx" onClick={closeWizard} aria-label="Close">✕</button>
              <ol className="ec-steps">
                {STEPS.map(s => (
                  <li key={s.n} className={`ec-step ${step === s.n ? 'on' : ''} ${step > s.n ? 'done' : ''}`}>
                    <span className="ec-stepn">{step > s.n ? '✓' : s.n}</span>
                    <span className="ec-steplabel">{s.label}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="ec-wizbody">
              {step === 1 && (
                <StepInfo draft={draft} setDraft={setDraft} mapData={mapData}
                          isAdmin={isAdmin} clubName={profile?.displayName || ''}
                          operator={club} clubDir={clubDir} api={api}
                          onClubAdded={c => setClubDir(list => [...list.filter(x => x.username !== c.username), c]
                            .sort((a, b) => a.displayName.localeCompare(b.displayName)))}
                          clubRoom={profile?.room || null} onSearching={setSearching} />
              )}
              {step === 2 && (
                <StepLocation draft={draft} setDraft={setDraft} mapData={mapData}
                              clubRoom={profile?.room || null}
                              pinning={pinning} setPinning={setPinning}
                              floor={pickedFloor} onPickFloor={setPickedFloor}
                              onPickBuilding={setPickedBuilding} />
              )}
              {step === 3 && (
                <StepPage draft={draft} setDraft={setDraft} logo={draftClub ? draftClub.logo : profile?.logo}
                          host={draftClub ? draftClub.displayName : (isAdmin && draft.host) || hostName}
                          onUpload={uploadBlockImage} />
              )}
              {step === 4 && (
                <StepShare e={draft} link={shareLink(draft)} onDownload={() => downloadQr(draft)}
                           approved={draftClub ? draftClub.approved : approved}
                           onCopied={() => setStatus('Link copied.')} />
              )}
            </div>

            <div className="ec-wizfoot">
              {step > 1
                ? <button className="ec-btn" onClick={() => setStep(step - 1)}>Back</button>
                : <button className="ec-btn" onClick={closeWizard}>Cancel</button>}
              <div className="ec-spacer" />
              {draft.id && step !== 4 && (
                <button className="ec-btn ec-del" onClick={() => removeEvent(draft)}>Delete</button>
              )}
              {step === 1 && (
                <button className="ec-btn ec-primary" disabled={!draft.name.trim()}
                        onClick={() => { setErr(''); setStep(2); }}>
                  Next: location
                </button>
              )}
              {/* Location is skippable on purpose. An event with no location is
                  a real thing to be part way through making, and refusing to
                  move on would strand a club that has not decided the room yet
                  — the map simply routes to the building, or says nothing. */}
              {step === 2 && (
                <button className="ec-btn ec-primary"
                        onClick={() => { setErr(''); setPinning(false); setStep(3); }}>
                  {draft.buildingSlug || draft.offCampus?.address?.trim()
                    ? 'Next: event page' : 'Skip for now'}
                </button>
              )}
              {step === 3 && (
                <button className="ec-btn ec-primary" disabled={busy} onClick={saveAndAdvance}>
                  {busy ? 'Saving…' : 'Save & get link'}
                </button>
              )}
              {step === 4 && (
                <button className="ec-btn ec-primary" onClick={closeWizard}>Done</button>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

/**
 * How to use this tool, as a video.
 *
 * The iframe is only built once the panel is actually opened. A YouTube embed
 * is several hundred KB and a third-party cookie the moment it exists, and this
 * screen is opened far more often to check an event than to learn the tool —
 * so a club that never opens it never loads it.
 *
 * `youtube-nocookie` for the same reason: nothing here needs a club's viewing
 * history to leave with Google.
 */
function TutorialVideo({ defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <details className="ec-tut" open={open}
             onToggle={e => setOpen(e.currentTarget.open)}>
      <summary>
        <span className="ec-tutplay" aria-hidden="true">▶</span>
        <span>
          <b>How to put your event on the map</b>
          <i>A short walkthrough — club details, the four steps, and the QR code.</i>
        </span>
      </summary>
      {/* Mounted only while open: see above. */}
      {open && (
        <div className="ec-tutframe">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${TUTORIAL_ID}?rel=0`}
            title="UMaps Event Map Creator tutorial"
            frameBorder="0" allowFullScreen
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      )}
      {/* The link out, for the club that would rather watch it full screen or
          send it to whoever is actually doing this next year. */}
      <a className="ec-tutlink" href={`https://youtu.be/${TUTORIAL_ID}`}
         target="_blank" rel="noopener noreferrer">Watch on YouTube ↗</a>
    </details>
  );
}

// ── Step 1: what, when, where ─────────────────────────────────────────────────
function StepInfo({ draft, setDraft, isAdmin, clubName, operator, clubDir, api, onClubAdded }) {
  const [adding, setAdding] = useState(null);   // null, or the name typed so far
  return (
    <>
      <label className="ec-field">Event name
        <input className="ec-input" autoFocus value={draft.name} placeholder="Introduction Night"
               onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
      </label>

      {/* ⚠️ OPERATOR ONLY, and it is not a vanity field. An event the operator
          makes on a club's behalf is stored under the OPERATOR's account, so
          `publicHost` falls back to "UMaps" unless this says otherwise — which
          is how "The Entrepreneurial Mindset" published an event under our name.
          A club never sees this: its own events resolve to its own display name,
          and a box letting a club type any host at all is a box letting it
          publish as somebody else. */}
      {isAdmin && (
        <div className="ec-field">Host <span className="ec-opt">operator only</span>
          <HostPicker clubs={clubDir} value={draft.owner || ''} operator={operator}
                      operatorName={clubName || 'UMaps'}
                      typedHost={draft.owner === operator ? draft.host || '' : ''}
                      onPick={owner => setDraft(d => ({ ...d, owner, host: '' }))}
                      onAdd={q => setAdding(q)} />
          <span className="ec-hint">
            The event is filed under the club you pick: it shows in that club's own
            dashboard, and the event page and campus map name it as the host.
            {draft.id && <>{' '}<strong>Changing it changes the event&rsquo;s link</strong>, so a
              QR already printed with the old one stops working — set it before sharing.</>}
          </span>
        </div>
      )}
      {adding !== null && (
        <NewClubDialog api={api} initialName={adding} clubs={clubDir}
                       onCancel={() => setAdding(null)}
                       onCreated={c => {
                         onClubAdded(c);
                         setDraft(d => ({ ...d, owner: c.username, host: '' }));
                         setAdding(null);
                       }} />
      )}
      <label className="ec-field ec-date">Date
        <input className="ec-input" type="date" value={draft.date}
               onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} />
      </label>
      <div className="ec-row">
        <div className="ec-field">Starts
          <TimeField value={draft.time}
                     onChange={t => setDraft(d => ({ ...d, time: t }))} />
        </div>
        <div className="ec-field">Ends <span className="ec-opt">optional</span>
          <TimeField value={draft.endTime} after={draft.time}
                     onChange={t => setDraft(d => ({ ...d, endTime: t }))} />
        </div>
      </div>
      <StepTags draft={draft} setDraft={setDraft} />
    </>
  );
}

/**
 * The five things a club can say beyond what and when.
 *
 * They are five toggles in a row, and each one that is on unfolds the fields it
 * needs underneath. A club scanning this sees the whole vocabulary at once —
 * which is the point, because these are opt-in and an option nobody notices is
 * an option nobody uses — while a club that wants none of it passes five chips
 * and a blank space.
 *
 * ⚠️ CUSTOM TAGS IS THE FIFTH AND IS LAST IN THE ROW, because it is the only
 * one whose words we do not choose. The four before it are the vocabulary; a
 * club reads them first and only reaches for its own phrasing once it has seen
 * that "free food" and "tickets" already exist. Put first, it would be the
 * easiest chip to reach for and clubs would hand-type tags this site already
 * understands and can act on.
 *
 * ⚠️ OFF CAMPUS LIVES HERE, on step 1, although it is a fact about the
 * LOCATION. It has to be answered before step 2, because it is what decides
 * which question step 2 asks: a campus map, or an address. Asking it on the
 * step it changes would mean the step rewriting itself under somebody who had
 * already started answering.
 */
function StepTags({ draft, setDraft }) {
  const set = (patch) => setDraft(d => ({ ...d, ...patch }));
  const tickets = draft.tickets || null;
  const setTickets = (patch) => set({ tickets: { url: '', price: null, ...tickets, ...patch } });

  // ⚠️ WHETHER THE FACULTY PANEL IS OPEN IS NOT WHETHER ANY ARE PICKED.
  //
  // The obvious version keys the chip off `faculties.length`, which means
  // ticking it has to select something — and whatever it selects is a
  // restriction the club did not choose and may never look at, on an event that
  // then goes out to one faculty. It also means un-ticking the last faculty
  // silently closes the panel.
  //
  // So the panel has its own state and the list stays exactly what the club
  // picked, including empty. An empty list with the panel open is a visible
  // half-answer with a warning on it; an empty list with the panel shut is
  // "open to everyone", which is what an event is by default.
  const [facOpen, setFacOpen] = useState(() => draft.faculties?.length > 0);

  // The custom-tag panel follows the faculty panel's rule and for the same
  // reason: open is not "has any". A club opens it, types nothing, and the
  // event has no custom tags — which is different from never having opened it
  // only in that the panel is showing.
  const customTags = draft.customTags || [];
  const [tagOpen, setTagOpen] = useState(() => customTags.length > 0);
  // What is in the box right now, which is NOT a tag until Add is pressed. Held
  // as the raw string so the counter counts what the club can see, including
  // the trailing space they are in the middle of typing.
  const [tagDraft, setTagDraft] = useState('');

  const typed = tagDraft.trim();
  const tagChars = Array.from(tagDraft).length;
  const full = customTags.length >= MAX_CUSTOM_TAGS;
  // ⚠️ THE CLIENT REFUSES EXACTLY WHAT THE STORE REFUSES, by asking the same
  // function rather than by re-implementing the rules: `normalizeCustomTags`
  // returns nothing for a tag that would not survive the save. A duplicate
  // fails here too, which is why the list is checked as a whole.
  const addable = !full && typed
    && normalizeCustomTags([...customTags, tagDraft]).length > customTags.length;
  const addTag = () => {
    if (!addable) return;
    set({ customTags: normalizeCustomTags([...customTags, tagDraft]) });
    setTagDraft('');
  };

  const chips = [
    {
      key: 'freeFood', on: !!draft.freeFood, label: 'Free food',
      // A tick, not a toggle: this one has nothing underneath it.
      toggle: () => set({ freeFood: !draft.freeFood }),
    },
    {
      key: 'faculties', on: facOpen, label: 'Faculty exclusive',
      // Turning it OFF empties the list rather than remembering it. A hidden
      // restriction that comes back when the chip is re-ticked is a restriction
      // nobody can see they still have.
      toggle: () => {
        if (facOpen) set({ faculties: [] });
        setFacOpen(o => !o);
      },
    },
    {
      key: 'tickets', on: !!tickets, label: 'Tickets',
      toggle: () => set({ tickets: tickets ? null : { url: '', price: null } }),
    },
    {
      key: 'offCampus', on: isOffCampus(draft), label: 'Off campus',
      // ⚠️ Ticking this THROWS AWAY the room. The server does the same on save
      // (see normalizeEvent) — done here too so the club watches it happen
      // rather than discovering later that the building it chose is gone.
      toggle: () => set(isOffCampus(draft)
        ? { offCampus: null }
        : { offCampus: { address: '' }, buildingSlug: '', buildingName: '', room: null }),
    },
    {
      key: 'customTags', on: tagOpen, label: 'Custom tags',
      // Switching it off empties the list, the rule the faculty chip follows:
      // tags that come back when the chip is re-ticked are tags nobody can see
      // they still have, and these are the ones printed in the club's own words.
      toggle: () => {
        if (tagOpen) { set({ customTags: [] }); setTagDraft(''); }
        setTagOpen(o => !o);
      },
    },
  ];

  return (
    <div className="ec-field">Tags <span className="ec-opt">optional</span>
      <div className="ec-chips" role="group" aria-label="Event tags">
        {chips.map(c => (
          <button key={c.key} type="button" aria-pressed={c.on}
                  className={`ec-chip ${c.on ? 'on' : ''}`} onClick={c.toggle}>
            {c.label}
          </button>
        ))}
      </div>

      {facOpen && (
        <div className="ec-sub">
          <b>Who is it for?</b>
          <i>
            Students in the app who picked one of these see the event; everyone
            else does not. On the web it says who it is for instead — nobody
            there has told us their faculty.
          </i>
          {/* The short names, in a scrolling box. The real ones are three
              hundred characters of "Clayton H. Riddell Faculty of…" and would
              be six lines of chip each; they ride on the title for the club
              matching this against a letter. */}
          <div className="ec-faclist">
            {FACULTIES.map(f => {
              // `|| []` throughout: an event saved before this feature existed
              // comes back with no `faculties` at all, and opening it in the
              // wizard would otherwise crash on the first render.
              const picked = draft.faculties || [];
              const on = picked.includes(f.id);
              return (
                <button key={f.id} type="button" title={f.label} aria-pressed={on}
                        className={`ec-chip small ${on ? 'on' : ''}`}
                        onClick={() => set({
                          faculties: on ? picked.filter(x => x !== f.id) : [...picked, f.id],
                        })}>
                  {f.short}
                </button>
              );
            })}
          </div>
          {/* Un-ticking the last one is the same as switching the tag off, and
              it has to say so: an empty list stored as a restriction would be
              an event nobody can see, which is the one failure here that is
              invisible from the club's side. */}
          {!draft.faculties?.length && (
            <span className="ec-warn">Pick at least one, or turn the tag off — this is open to everyone.</span>
          )}
        </div>
      )}

      {tickets && (
        <div className="ec-sub">
          <b>Where do people buy them?</b>
          <label className="ec-field">Ticket link
            <input className="ec-input" value={tickets.url || ''} inputMode="url"
                   placeholder="https://…"
                   onChange={e => setTickets({ url: e.target.value })} />
          </label>
          <label className="ec-field">Price <span className="ec-opt">optional</span>
            <div className="ec-price">
              <span>$</span>
              <input className="ec-input" type="number" min="0" step="0.01" placeholder="10"
                     value={tickets.price ?? ''}
                     onChange={e => setTickets({
                       // '' is "not said", and 0 is free — Number('') is 0, so
                       // the empty box would have published every event as free.
                       price: e.target.value === '' ? null : Number(e.target.value),
                     })} />
            </div>
            <span className="ec-hint">
              {tickets.price === 0
                ? 'The button will say “Get free tickets”.'
                : `The button will say “Buy tickets${priceLabel(tickets.price) ? `: ${priceLabel(tickets.price)}` : ''}”.`}
            </span>
          </label>
        </div>
      )}

      {/* The address itself is asked for on the LOCATION step, which is where
          "where is it" belongs and which this chip has just changed the meaning
          of. Said out loud here, because a chip whose only effect is on the
          next screen otherwise looks like it did nothing. */}
      {isOffCampus(draft) && (
        <div className="ec-sub">
          <i>No campus building for this one — the next step asks for an address instead.</i>
        </div>
      )}

      {tagOpen && (
        <div className="ec-sub">
          <b>Anything else worth a badge?</b>
          <i>
            Up to {MAX_CUSTOM_TAGS}, {MAX_CUSTOM_TAG_CHARS} characters each, shown
            exactly as you type them — “Bring a mug”, “Cash only”, “Costumes”.
            They sit beside the tags above, in plain grey: those are facts the map
            understands, these are your own words.
          </i>
          {/* One line, the way the ticket link above is one line: type it, press
              Add, and it becomes a pill you can take off again. The counter is
              beside the box rather than under it, because 15 characters runs out
              in the middle of a phrase and a club needs to see that happening,
              not find out when Add stops working. */}
          <div className="ec-tagadd">
            <input className="ec-input" value={tagDraft} maxLength={MAX_CUSTOM_TAG_CHARS}
                   placeholder={full ? 'That is all three' : 'Bring a mug'}
                   disabled={full}
                   aria-label="Custom tag"
                   onChange={ev => setTagDraft(ev.target.value)}
                   // Enter adds, because this input sits in a wizard step whose
                   // own form control is "Next" — without it, Enter would walk
                   // the club to the following step carrying an untyped tag.
                   onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); addTag(); } }} />
            <span className={`ec-tagcount${tagChars >= MAX_CUSTOM_TAG_CHARS ? ' at' : ''}`}>
              {tagChars}/{MAX_CUSTOM_TAG_CHARS}
            </span>
            {/* Disabled at three, and disabled for a duplicate or a blank — the
                three things the store would silently drop. A tag that vanishes
                on save is the one failure here the club cannot see. */}
            <button type="button" className="ec-btn" onClick={addTag} disabled={!addable}>Add</button>
          </div>
          {customTags.length > 0 && (
            <div className="ec-taglist">
              {customTags.map(t => (
                <span key={t.toLowerCase()} className="ec-taghas">
                  {t}
                  <button type="button" aria-label={`Remove ${t}`}
                          onClick={() => set({ customTags: customTags.filter(x => x !== t) })}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

/**
 * A time, in the three pieces people actually say one in.
 *
 * `<input type="time">` was the obvious control and the wrong one on the screen
 * this is used on: its am/pm segment is typed — you press A or P, or arrow
 * through it — which nobody guesses, and it starts on an empty minute field
 * that has to be filled in even though club events start on the hour.
 *
 * So: an hour, minutes in fives DEFAULTING TO :00, and am/pm as two buttons you
 * can see the state of. The value going in and out is still "HH:MM" in 24-hour
 * time, which is what the store keeps and what `eventDate` parses — this is a
 * way of typing it, not a second format.
 */
// Clock order — 12 leads, the way a twelve-hour clock is read and every other
// time control lists it.
const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
// Fives. Every club event that has ever been posted starts on one of these, and
// a free minute field is twelve keystrokes of precision nobody wanted.
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);        // 00, 05 … 55

function TimeField({ value, onChange, after }) {
  const cur = parseHm(value);
  const set = (h12, m, pm) => onChange(toHm(h12, m, pm));

  const pickHour = (h12) => {
    if (h12 === '') { onChange(''); return; }
    const n = Number(h12);
    // ⚠️ MINUTES DEFAULT TO :00. Picking the hour is the whole answer for most
    // events, and leaving the minutes empty made every single one a second
    // decision about nothing.
    const min = cur.m ?? 0;
    set(n, min, cur.pm ?? guessPm(n, min, after));
  };

  return (
    <div className="ec-time">
      <select className="ec-input ec-timesel" value={cur.h12 ?? ''}
              onChange={e => pickHour(e.target.value)} aria-label="Hour">
        <option value="">--</option>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="ec-timecolon">:</span>
      <select className="ec-input ec-timesel" value={cur.m ?? ''} disabled={cur.h12 == null}
              onChange={e => set(cur.h12, Number(e.target.value), cur.pm)} aria-label="Minutes">
        {cur.m == null && <option value="">--</option>}
        {MINUTES.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
      </select>
      {/* Two buttons rather than a third dropdown: it is the one part of a time
          with exactly two answers, and its state has to be readable at a glance
          — an event published at 7am instead of 7pm is the mistake this whole
          control exists to prevent. */}
      <div className="ec-ampm" role="group" aria-label="AM or PM">
        {[false, true].map(pm => (
          <button key={String(pm)} type="button"
                  className={`ec-ampmbtn ${cur.pm === pm ? 'on' : ''}`}
                  disabled={cur.h12 == null} aria-pressed={cur.pm === pm}
                  onClick={() => set(cur.h12, cur.m ?? 0, pm)}>
            {pm ? 'PM' : 'AM'}
          </button>
        ))}
      </div>
      {value && (
        <button type="button" className="ec-timeclear" onClick={() => onChange('')}
                title="Clear the time">✕</button>
      )}
    </div>
  );
}


/**
 * The floor-plan palette, matching the pathfinder at /umanitoba.
 *
 * ⚠️ COPIED, not imported. `POLY_COLORS` is a module-private const inside
 * `src/pathfinder/useRouteLayer.js`, and that folder is a copy-out of
 * UMapsTools — adding an export there to serve this file would put a local edit
 * in a synced tree. The same trade `lib/parking.js` makes in the other
 * direction. If the palette changes there, change it here: a club picking a
 * room should be looking at the map its attendees will.
 *
 * The white outline is the paper the plan is printed on; the rooms do not tile
 * the footprint, so the corridors and gaps between wings ARE that white showing
 * through. Stairs and lifts keep their own colours because they are the things
 * somebody navigates by.
 */
const POLY_COLORS = {
  outline:  { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 0   },
  room:     { fill: '#FCF8EF', fillOp: 0.85, stroke: '#dbdbdb', strokeW: 1.5 },
  stair:    { fill: '#c4956a', fillOp: 0.9,  stroke: '#a0724a', strokeW: 1.5 },
  elevator: { fill: '#b0bec5', fillOp: 0.9,  stroke: '#78909c', strokeW: 1.5 },
  blocked:  { fill: '#000000', fillOp: 0.9,  stroke: '#000000', strokeW: 1.5 },
  tunnel:   { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 0   },
  parking:  { fill: '#66bb6a', fillOp: 0.4,  stroke: '#2e7d32', strokeW: 1.5 },
};
const polyStyle = (type) => POLY_COLORS[type] ?? POLY_COLORS.room;

// What a room turns when it is pointed at or chosen. Google's amber rather than
// the tool's indigo: it has to be legible over beige rooms and white corridors
// AND over satellite imagery where a room hangs off the edge of the plan, and
// indigo at 30% over #FCF8EF reads as grey.
const ACCENT = '#fbbc04';

/** The centre of a ring, for hanging a label on. */
function ringCentroid(ring) {
  let lat = 0, lng = 0;
  for (const p of ring) { lat += p.lat; lng += p.lng; }
  return { lat: lat / ring.length, lng: lng / ring.length };
}

/**
 * Every room polygon on one floor of one building, for drawing.
 *
 * Only the rooms — the `outline` type is the footprint and is drawn separately,
 * in a different weight, because "the building" and "a room in it" are not the
 * same kind of thing to look at.
 */
function buildingRooms(mapData, slug, floor) {
  const b = mapData?.buildings?.find(x => x.slug === slug);
  if (!b?.polygonsByFloor) return [];
  return (b.polygonsByFloor.get(floor) || [])
    .filter(p => p.type !== 'outline' && p.ring?.length >= 3);
}

/**
 * The white paper ONE floor is printed on.
 *
 * ⚠️ Not `b.outlines`, which is every floor's outline in one array — six
 * stacked copies for a six-storey building, drawn on top of each other whatever
 * floor was being looked at. Each floor has its own, in the footprint stub as
 * well as in the interior, so this is available before anything is fetched.
 * The building's own outlines are the fallback for a floor that has none.
 */
function floorOutlines(mapData, slug, floor) {
  const b = mapData?.buildings?.find(x => x.slug === slug);
  if (!b) return [];
  const here = (b.polygonsByFloor?.get(floor) || [])
    .filter(p => p.type === 'outline' && p.ring?.length >= 3)
    .map(p => p.ring);
  return here.length ? here : (b.outlines || []);
}

/**
 * The room object the rest of the tool stores, from a routing-index entry.
 *
 * Shared by the search box and by a tap on the map, so the two cannot disagree
 * about what picking a room means. It is the shape `normalizeRoom` keeps,
 * including `buildingName`: a saved room has to be able to name its own
 * building, because what renders it later has no campus index loaded.
 */
const roomFromEntry = (entry) => ({
  building: entry.building,
  buildingName: entry.buildingName || '',
  roomNumber: entry.roomNumber,
  roomName: entry.roomName || '',
  label: roomLabel(entry),
  nodeIds: entry.nodeIds || (entry.nodeId ? [entry.nodeId] : []),
});

/**
 * The floors a building has.
 *
 * Read straight off the building, which carries them from the outlines bundle
 * before its interior has been fetched at all — so the floor switcher is right
 * the moment a building is picked rather than a request later. Measured against
 * the snapshot: no building has a nav node on a floor its plans do not cover, so
 * this list is also every floor a pin could be dropped on.
 */
function buildingFloors(mapData, slug) {
  const b = mapData?.buildings?.find(x => x.slug === slug);
  return b?.floors?.length ? b.floors : [1];
}

/** The floor to open a building on: the ground floor if it has one. */
const groundFloor = (floors) => (floors.includes(1) ? 1 : (floors[0] ?? 1));

// Two forms of the same thing: the pill is a tab in a row of tabs and has room
// for one character; the sentence beside it is what a club reads. -1 is the
// tunnel level and 0 the outdoors, which is how the router codes them.
const floorShort = (f) => (f === -1 ? 'T' : f === 0 ? 'G' : String(f));
const floorWord = (f) => (f === -1 ? 'Tunnel level' : f === 0 ? 'Ground floor' : `Floor ${f}`);

/**
 * Which floor a saved location is on.
 *
 * A pin says so itself — it is stored with the point, because a point without a
 * floor is two different places in a six-storey building. A room does not need
 * to: its door node knows, and re-deriving it beats storing a second copy that
 * can fall out of step with the room it describes.
 */
function roomFloor(mapData, room) {
  if (!room) return null;
  if (Number.isFinite(room.floor)) return room.floor;
  const id = room.nodeIds?.[0] || room.nodeId;
  const n = id ? mapData?.nodes?.get(id) : null;
  return Number.isFinite(n?.floor) ? n.floor : null;
}

/** Does this index entry have a door on `floor`? */
function entryOnFloor(mapData, entry, floor) {
  return entryDoorFloors(mapData, entry).includes(floor);
}

/** The floor an index entry reads as: its first door's, which is the one the map
 *  frames when it is picked (`getRoomLocation` uses the same node). */
function entryFloor(mapData, entry) {
  const f = entryDoorFloors(mapData, entry);
  return f.length ? f[0] : null;
}

/** Every floor this entry has a door on — a lecture theatre entered from two
 *  levels is on both, and saying so is true both times. */
function entryDoorFloors(mapData, entry) {
  const ids = entry?.nodeIds?.length ? entry.nodeIds : (entry?.nodeId ? [entry.nodeId] : []);
  const out = [];
  for (const id of ids) {
    const f = mapData?.nodes?.get(id)?.floor;
    if (Number.isFinite(f) && !out.includes(f)) out.push(f);
  }
  return out;
}

/**
 * A room's number, drawn on the room.
 *
 * The pathfinder does not label rooms — it highlights the one you asked for and
 * that is the answer. Here the map is the question: somebody is looking for the
 * room their event is in, or deciding which corridor to pin, and an unlabelled
 * floor plan makes them count doors. So the numbers go on, and only in close
 * (`ROOM_LABEL_ZOOM`) where they fit inside the shapes they name.
 */
function createRoomLabel(map, position, text) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);
  class RoomLabel extends gmaps.OverlayView {
    onAdd() {
      const div = document.createElement('div');
      div.className = 'ec-roomlabel';
      div.textContent = text;
      this.div = div;
      // markerLayer, not overlayLayer: overlayLayer is where Maps draws the
      // polygons, so a label there stacks by insertion order and disappears
      // under the next polygon added. Same reasoning as the pathfinder's
      // building labels.
      this.getPanes().markerLayer.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const pt = this.getProjection()?.fromLatLngToDivPixel(latLng);
      if (!pt) return;
      // Hidden rather than removed below the threshold: there are dozens per
      // floor and they come straight back on the next zoom in.
      const show = (map.getZoom() ?? 0) >= ROOM_LABEL_ZOOM;
      this.div.style.display = show ? '' : 'none';
      this.div.style.left = `${pt.x}px`;
      this.div.style.top = `${pt.y}px`;
    }
    onRemove() { this.div?.remove(); this.div = null; }
  }
  const overlay = new RoomLabel();
  overlay.setMap(map);
  return { remove: () => overlay.setMap(null), redraw: () => overlay.draw() };
}

/** Metres between two lat/lngs. Equirectangular — fine at building scale. */
function metresBetween(a, b) {
  const R = 6371000, toRad = Math.PI / 180;
  const x = (b.lng - a.lng) * toRad * Math.cos(((a.lat + b.lat) / 2) * toRad);
  const y = (b.lat - a.lat) * toRad;
  return Math.sqrt(x * x + y * y) * R;
}

/**
 * The nav node a dropped pin should route to.
 *
 * A pin is a place with no room — the middle of an atrium, a table in a
 * corridor — so it has no doors to route to and something has to stand in for
 * them. The nearest node ON THE SAME FLOOR wins outright; a node on another
 * floor is only considered if the floor has none at all, because the stairwell
 * directly below somebody is not where they are going.
 *
 * The same rule the Eng Tour's Create Spot uses, for the same reason: a
 * destination the router cannot reach is worse than no destination.
 */
function nearestNodeTo(mapData, slug, point, floor) {
  let best = null, bestD = Infinity, bestOff = null, bestOffD = Infinity;
  for (const n of mapData.nodes.values()) {
    if (n.building !== slug) continue;
    const d = metresBetween(point, { lat: n.lat, lng: n.lng });
    if (n.floor === floor) {
      if (d < bestD) { bestD = d; best = n; }
    } else if (d < bestOffD) { bestOffD = d; bestOff = n; }
  }
  return best || bestOff || null;
}

/**
 * Step 2: WHERE. A building first, then a room in it or a pin on it.
 *
 * The old version was one search box over every room on campus, sitting in the
 * middle of the event-details form. Two things were wrong with that. A club
 * that knows the building but not the room number had nowhere to start — the
 * box only answered room numbers — and there was no way at all to say "the
 * atrium", "the concourse", "the table by the north doors", which is where a
 * great many club events actually happen.
 *
 * So: pick the building, then the FLOOR, and the map goes there and draws that
 * floor's rooms. Then either type the room number, or drop a pin. The pin snaps
 * to the nearest nav node on that floor so the router has something real to walk
 * to.
 *
 * The floor is a step in its own right because a building is a stack of plans
 * and only one of them can be on screen. Without it the room search offered
 * every 200-something in the building against a picture of floor 1, and a pin
 * could only ever be dropped on the ground floor — so the two things a club can
 * choose here were both quietly limited to one storey of a six-storey building.
 */
function StepLocation({ draft, setDraft, mapData, clubRoom, pinning, setPinning,
                        floor, onPickFloor, onPickBuilding }) {
  const [q, setQ] = useState('');
  const slug = draft.buildingSlug || null;
  const offCampus = isOffCampus(draft);
  const floors = useMemo(() => (slug ? buildingFloors(mapData, slug) : []), [mapData, slug]);

  const buildings = useMemo(() => {
    const all = (mapData?.buildings || []).filter(b => b.name && !PSEUDO_BUILDINGS.has(b.slug));
    const needle = q.trim();
    const list = needle ? searchBuildings(all, needle, 8) : all;
    return [...list].sort((a, b) => a.name.localeCompare(b.name)).slice(0, needle ? 8 : 400);
  }, [mapData, q]);

  /** Choosing a building clears whatever was chosen inside the old one. */
  const pickBuilding = (b) => {
    onPickBuilding(b.slug);
    // Its own ground floor, not whichever floor the last building was on: "3"
    // means different things in two buildings, and one of them may not have a
    // third floor at all.
    onPickFloor(groundFloor(buildingFloors(mapData, b.slug)));
    setPinning(false);
    setDraft(d => ({ ...d, buildingSlug: b.slug, buildingName: b.name, room: null }));
    setQ('');
  };

  const clearAll = () => {
    onPickBuilding(null);
    setPinning(false);
    setDraft(d => ({ ...d, buildingSlug: '', buildingName: '', room: null }));
  };

  const setRoom = (room) => setDraft(d => ({
    ...d,
    buildingSlug: room?.building || d.buildingSlug,
    buildingName: room?.buildingName || d.buildingName,
    room,
  }));

  // ── Off campus: an address, and no campus map to point at ──
  //
  // Placed after every hook above, which all run and produce nothing useful for
  // this branch — cheap, and the alternative is a component that changes its
  // hook order when a chip is ticked.
  if (offCampus) {
    const address = draft.offCampus?.address || '';
    return (
      <div className="ec-loc">
        <div className="ec-field">Address
          <input className="ec-input" autoFocus value={address} inputMode="text"
                 placeholder="1 Forks Market Rd, Winnipeg, MB"
                 onChange={e => setDraft(d => ({ ...d, offCampus: { address: e.target.value } }))} />
          <span className="ec-hint">
            As you’d type it into Google Maps — a pasted Maps link works too.
          </span>
        </div>
        {/* What an attendee actually gets, said plainly on the screen where it
            is decided. This is the one location that the campus map cannot
            answer, and a club should know that before the posters are printed
            rather than from somebody who turned up at the wrong building. */}
        <div className="ec-note">
          <b>This event won’t appear on the campus map.</b> There is nowhere honest to
          put a pin for somewhere that isn’t on campus, and we can’t draw a walking
          route to it. Attendees see the address and a link that opens it in Google Maps,
          and the event still shows in the events calendar and on its own page.
        </div>
        {/* The link, as it will be, so a mistyped address is caught here. */}
        {address.trim() && (
          <a className="ec-suggest" href={offCampusLink(address)}
             target="_blank" rel="noopener noreferrer">
            Check it opens the right place
            <span>Opens Google Maps in a new tab</span>
          </a>
        )}
      </div>
    );
  }

  // ── No building yet: the only question on screen ──
  if (!slug) {
    return (
      <div className="ec-loc">
        <div className="ec-field">Which building?
          <input className="ec-input" autoFocus value={q} onChange={e => setQ(e.target.value)}
                 placeholder={mapData ? 'Search buildings — “EITC”, “University Centre”' : 'Loading campus…'}
                 disabled={!mapData} />
        </div>
        {/* The club's own room, offered as a shortcut before any typing — a club
            running an event where it always meets is the common case, and
            searching for something you already told us is work repeated. */}
        {clubRoom?.building && !q.trim() && (
          <button className="ec-suggest" onClick={() => setRoom(clubRoom)}>
            Use our club room
            <span>{clubRoom.label || clubRoom.roomNumber}{clubRoom.buildingName ? ` · ${clubRoom.buildingName}` : ''}</span>
          </button>
        )}
        <ul className="ec-blist">
          {buildings.map(b => (
            <li key={b.slug}>
              <button onClick={() => pickBuilding(b)}>{b.name}</button>
            </li>
          ))}
          {mapData && !buildings.length && (
            <li className="ec-bnone">No building matches that.</li>
          )}
        </ul>
      </div>
    );
  }

  // ── A building is chosen: now the floor, then the room or a pin ──
  const room = draft.room;
  const chosenFloor = roomFloor(mapData, room);
  // Shown whatever is chosen. The map keeps drawing the whole floor now, so
  // flipping storeys with a room already picked is a real thing to do — look at
  // floor 4, tap a room there, and the choice moves.
  const showFloors = floors.length > 1;
  return (
    <div className="ec-loc">
      <div className="ec-locbar">
        <div className="ec-locwhere">
          <strong>{draft.buildingName || slug}</strong>
          <span>
            {room?.roomNumber
              ? `Room ${room.roomNumber}`
              : room?.point
                ? (room.label || 'Pinned spot')
                : 'Tap a room on the map, or search for it'}
          </span>
        </div>
        <button className="ec-btn" onClick={clearAll}>Change building</button>
      </div>

      {/* The floors, as tabs. A row rather than a dropdown: there are rarely
          more than six, they are the shortest labels on the screen, and the one
          you are on has to be visible while you are looking at the plan it
          draws. It scrolls sideways in the rare building that has more. */}
      {showFloors && (
        <div className="ec-floors" role="group" aria-label="Floor">
          {floors.map(f => (
            <button key={f} className={`ec-floor ${f === floor ? 'on' : ''}`}
                    aria-pressed={f === floor} title={floorWord(f)}
                    onClick={() => onPickFloor(f)}>
              {floorShort(f)}
            </button>
          ))}
          <span className="ec-floorword">{floorWord(floor)}</span>
        </div>
      )}

      {room ? (
        <>
          <div className="ec-chosen">
            <div className="ec-chosenmain">
              <strong>{room.label || (room.roomNumber ? `Room ${room.roomNumber}` : 'Pinned spot')}</strong>
              <span>
                {/* The floor is named here because it is the one part of a
                    location that cannot be seen on a flat map: a pin on floor 3
                    and a pin on floor 1 are the same dot. */}
                {chosenFloor != null && `${floorWord(chosenFloor)} · `}
                {room.point && !room.roomNumber
                  ? 'Attendees are walked to the nearest point on the route network.'
                  : 'Attendees are routed to the nearest entrance, then to this room.'}
              </span>
            </div>
            <button className="ec-btn" onClick={() => { setDraft(d => ({ ...d, room: null })); setPinning(false); }}>
              Change
            </button>
          </div>
          {/* A pin has no room number to be called by, so it needs a name or it
              reads as "Pinned spot" on the event page and on the map. A room
              does not: its number is its name, and letting somebody retype it
              is how the two end up disagreeing. */}
          {room.point && !room.roomNumber && (
            <label className="ec-field">What is this spot called?
              <input className="ec-input" value={room.label === 'Pinned spot' ? '' : room.label}
                     placeholder="Main atrium, by the stairs" maxLength={80}
                     onChange={e => setDraft(d => ({
                       ...d, room: { ...d.room, label: e.target.value || 'Pinned spot' },
                     }))} />
              <span className="ec-hint">Shown to attendees instead of a room number.</span>
            </label>
          )}
          <button className="ec-btn ec-pinbtn" onClick={() => { setPinning(true); }}>
            {room.point ? 'Move the pin' : 'Point at a spot instead'}
          </button>
        </>
      ) : pinning ? (
        <div className="ec-pinning">
          {/* The floor is in the instruction, not just in the tabs above: a tap
              is about to be filed on one of six plans, and this is the moment
              somebody would notice they are looking at the wrong one. */}
          <strong>Tap the spot on {floorWord(floor).toLowerCase()}.</strong>
          <span>
            For places with no room number — an atrium, a concourse, a table in a hallway.
            The rooms of that floor are drawn behind so you can see where you are pointing;
            switch floors above if the spot is on another one.
          </span>
          <button className="ec-btn" onClick={() => setPinning(false)}>Cancel</button>
        </div>
      ) : (
        <RoomPicker value={null} onChange={setRoom} mapData={mapData} inBuilding={slug}
                    onFloor={floors.length > 1 ? floor : null}
                    /* ⚠️ Deliberately NOT reporting `searching` here. That flag
                       lets the card grow to the full height of the stage, which
                       is right when a campus-wide search wants ten results
                       visible — and wrong here, where growing the card hides
                       the map somebody is about to point at. The list scrolls
                       inside a short box instead. */
                    /* The other half of the answer, and the one nothing offered
                       before. Above the results rather than after them, so it is
                       on screen at the moment somebody realises their event has
                       no room number. */
                    afterInput={(
                      <button className="ec-btn ec-pinbtn" onClick={() => setPinning(true)}>
                        Not in a room — point at it on the map
                      </button>
                    )} />
      )}
    </div>
  );
}

/**
 * One box that searches every room on campus, the way the pathfinder's own
 * search does — same index, same `searchRooms` scoring, same labels.
 *
 * It replaced a Building dropdown followed by a free-text room number, which
 * asked the club to know two things ("which of 38 buildings is this in?" and
 * "what is the room called in our data?") to say one. Typing "204" now lists
 * every 204 on campus with its building beside it, and "eitc 204" narrows it —
 * and because the answer comes out of the routing index, a room that is picked
 * here is a room the map can definitely route to.
 *
 * Two screens ask this question — an event's location and the club's own room —
 * so it takes a value and hands one back rather than reaching into a draft. The
 * shape it emits is the one the store keeps (`normalizeRoom`), including
 * `buildingName`: a saved room has to be able to name its own building, because
 * the places that render one later do not have the campus index loaded.
 */
function RoomPicker({ value, onChange, mapData, onSearching, suggestion, suggestionLabel,
                     inBuilding, onFloor, afterInput }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  /**
   * `inBuilding` narrows the index before scoring, rather than filtering after.
   *
   * On the location step the building is already answered, so "204" must mean
   * "204 in THIS building" — searching campus-wide there would put twelve other
   * 204s above the one they meant, and the top hit would be whichever building
   * sorted first. Without the prop this is the original campus-wide search,
   * which is still what the club-room field wants.
   *
   * `onFloor` narrows the BROWSING list, and deliberately not the search.
   *
   * An empty box lists the floor whose plan is on the map, because that list is
   * read against the picture: offering a fourth-floor room beside a drawing of
   * floor 1 is how somebody picks a room they have never seen and believes they
   * recognised it. But a club that types "405" knows the number and not the
   * storey — refusing it because the map happens to be showing floor 1 would be
   * the tool withholding an answer it has — so a typed query searches the whole
   * building and each row says which floor it is on. Picking one takes the map
   * there.
   */
  const onFloorOf = useCallback(
    (entry) => (entry ? entryFloor(mapData, entry) : null), [mapData]);

  const results = useMemo(() => {
    if (!mapData) return [];
    let index = mapData.roomIndex || [];
    if (inBuilding) index = index.filter(r => r.building === inBuilding);
    // Inside one building an empty box lists what is there, because a club that
    // does not know the number can recognise it; campus-wide that would be five
    // thousand rooms and is left as a prompt to type.
    if (!q.trim()) {
      if (!inBuilding) return [];
      const here = onFloor == null ? index : index.filter(r => entryOnFloor(mapData, r, onFloor));
      return here.slice(0, 12);
    }
    return searchRooms(index, q, 12);
  }, [mapData, q, inBuilding, onFloor]);

  // The wizard grows while the list is showing, so it has to know. Reported from
  // an effect rather than from each handler, so closing the list by picking a
  // room, by blurring, or by the results emptying all report the same way.
  const listing = (open || !!inBuilding) && !value && (!!results.length || !!q.trim());
  useEffect(() => { onSearching?.(listing); }, [listing, onSearching]);
  useEffect(() => () => onSearching?.(false), [onSearching]);

  const choose = (entry) => {
    onChange(roomFromEntry(entry));
    setQ(''); setOpen(false);
  };

  if (value) {
    return (
      <div className="ec-chosen">
        <div className="ec-chosenmain">
          <strong>{value.label || value.roomNumber}</strong>
          <span>{value.buildingName}{value.roomNumber ? ` · Room ${value.roomNumber}` : ''}</span>
        </div>
        <button className="ec-btn" onClick={() => onChange(null)}>Change</button>
      </div>
    );
  }

  return (
    <div className="ec-search">
      {/* Room NUMBER leads the placeholder, deliberately. Friendly names are
          searched too, but only 2 of the snapshot's 5,247 rooms currently
          have one — leading with "SSA Lounge" would advertise a way of
          searching that finds almost nothing. */}
      <input
        className="ec-input" value={q}
        placeholder={!mapData ? 'Loading campus rooms…'
          : inBuilding ? 'Room number, any floor — “204”' : 'Search any room — “204” or “EITC 204”'}
        disabled={!mapData}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        // A blur that fires before the click would close the list out from
        // under the finger, so it is deferred by a frame.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {/* ⚠️ RENDERED BETWEEN THE INPUT AND THE RESULTS, on purpose. The results
          list is up to 400px tall, so anything after it lands below the fold of
          a card capped at 58% of the screen — measured: the "not in a room"
          button ended up at y=680 in an 850px stage, under the list, where
          `elementFromPoint` returned the map instead of the button. A club
          would have clicked it and had nothing happen. */}
      {afterInput}
      {suggestion && !q.trim() && (
        <button className="ec-suggest" onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange(suggestion); setOpen(false); }}>
          {suggestionLabel}
          <span>{suggestion.label || suggestion.roomNumber}{suggestion.buildingName ? ` · ${suggestion.buildingName}` : ''}</span>
        </button>
      )}
      {(open || inBuilding) && !!results.length && (
        <ul className={`ec-results ${inBuilding ? 'ec-results-in' : ''}`}>
          {results.map(r => {
            // Said only when the row is somewhere other than the plan on screen
            // — on the floor being looked at it would be the same word printed
            // beside all twelve rows, which is how a label stops being read.
            const rf = inBuilding && onFloor != null ? onFloorOf(r) : null;
            return (
              <li key={`${r.building}::${r.roomNumber}`}>
                <button onMouseDown={e => e.preventDefault()} onClick={() => choose(r)}>
                  <span className="ec-rlabel">{roomLabel(r)}</span>
                  {rf != null && rf !== onFloor && <span className="ec-rfloor">{floorWord(rf)}</span>}
                  {roomNumberBadge(r) && <span className="ec-rbadge">{roomNumberBadge(r)}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {(open || inBuilding) && mapData && q.trim() && !results.length && (
        <div className="ec-noresults">
          {inBuilding
            ? 'No room in this building matches that — on any floor.'
            : 'No room matches that. Try the room number on its own.'}
        </div>
      )}
      {/* A floor with no numbered rooms in the data is a real thing — a
          mechanical level, a concourse nobody has drawn doors for — and an
          empty box with no explanation reads as a search that is broken rather
          than as an answer. */}
      {inBuilding && mapData && !q.trim() && !results.length && (
        <div className="ec-noresults">
          No rooms are mapped{onFloor != null ? ` on ${floorWord(onFloor).toLowerCase()}` : ''} — point at
          the spot on the map instead.
        </div>
      )}
    </div>
  );
}

// ── Step 2: the event page, built inside its own banner ───────────────────────
function StepPage({ draft, setDraft, logo, host, onUpload }) {
  return (
    <div className="ec-page">
      {/* Not a preview toggle — the only view. The blocks are added directly
          under the banner an attendee will read them under, so what is being
          built and what it will look like are the same screen. */}
      <div className="ec-pbanner">
        {logo && <img src={logo} alt="" />}
        <div>
          <strong>{draft.name || 'Untitled event'}</strong>
          <span><b>{host}</b> · {fmtWhen(draft)}</span>
          <span>{eventWhere(draft) || 'No location chosen'}</span>
        </div>
      </div>
      {/* The same rule and repeated title an attendee sees, so the editor is
          laid out like the page and not merely near it. Read-only here: the name
          is edited on step 1, and two fields writing one value is how they end
          up disagreeing. */}
      <hr className="ec-prule" />
      <h2 className="ec-ptitle">{draft.name || 'Untitled event'}</h2>

      <BlockEditor blocks={draft.blocks} onUpload={onUpload}
                   onChange={b => setDraft(d => ({ ...d, blocks: b }))} />
    </div>
  );
}

// ── The operator's Host picker ────────────────────────────────────────────────
/**
 * Which club an event is BY — searched, picked, or made on the spot.
 *
 * The operator puts events up for one club after another, so this is built to
 * be typed into: open it, type three letters of the name or the initialism,
 * Enter. "UMaps" is an option of its own (an event that is ours), and the last
 * row makes a new club from whatever was typed.
 *
 * A club with no logo or contact email cannot host — the server refuses the
 * save (`clubReady`) — so it is shown, but disabled, with why.
 */
function HostPicker({ clubs, value, operator, operatorName, typedHost, onPick, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (ev) => { if (!boxRef.current?.contains(ev.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const needle = q.trim().toLowerCase();
  const rows = useMemo(() => {
    const own = { username: operator, displayName: operatorName, own: true, ready: true, approved: true };
    const all = [own, ...clubs];
    if (!needle) return all;
    // Starts-with first, then contains: "cs" should put CSSA above Physics.
    const score = (c) => {
      const names = [c.displayName, c.shortName, c.username].map(v => String(v || '').toLowerCase());
      if (names.some(n => n.startsWith(needle))) return 0;
      if (names.some(n => n.includes(needle))) return 1;
      return 2;
    };
    return all.map(c => [score(c), c]).filter(([n]) => n < 2)
      .sort((a, b) => a[0] - b[0]).map(([, c]) => c);
  }, [clubs, needle, operator, operatorName]);
  // The "add" row is the last one, so arrowing past the list lands on it.
  const count = rows.length + 1;

  const current = value === operator
    ? { displayName: typedHost || operatorName, own: !typedHost, typed: !!typedHost }
    : clubs.find(c => c.username === value);
  const choose = (c) => {
    if (!c.ready) return;
    onPick(c.username); setOpen(false); setQ('');
  };
  const key = (ev) => {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setHi(h => Math.min(h + 1, count - 1)); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (ev.key === 'Escape') { setOpen(false); }
    else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (hi < rows.length) choose(rows[hi]);
      else { setOpen(false); onAdd(q.trim()); }
    }
  };

  return (
    <div className="ec-hostpick" ref={boxRef}>
      <button type="button" className={`ec-input ec-hostbtn${value ? '' : ' empty'}`}
              onClick={() => { setOpen(o => !o); setHi(0); }}>
        {current ? <HostRow c={current} /> : <span className="ec-dim">Pick the club hosting this…</span>}
        <span className="ec-hostcaret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="ec-hostpanel">
          <input className="ec-input" autoFocus value={q} placeholder="Search clubs by name or initials"
                 onChange={ev => { setQ(ev.target.value); setHi(0); }} onKeyDown={key} />
          <ul className="ec-hostlist" role="listbox">
            {rows.map((c, i) => (
              <li key={c.username} role="option" aria-selected={c.username === value}
                  aria-disabled={!c.ready}
                  className={`${i === hi ? 'hi' : ''}${c.ready ? '' : ' off'}`}
                  onMouseEnter={() => setHi(i)} onMouseDown={ev => { ev.preventDefault(); choose(c); }}>
                <HostRow c={c} />
              </li>
            ))}
            {!rows.length && <li className="ec-hostnone">No club matches “{q.trim()}”.</li>}
            <li className={`ec-hostadd${hi === rows.length ? ' hi' : ''}`}
                onMouseEnter={() => setHi(rows.length)}
                onMouseDown={ev => { ev.preventDefault(); setOpen(false); onAdd(q.trim()); }}>
              + Add a new club{q.trim() ? <> “{q.trim()}”</> : ''}…
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

/** One club as the picker draws it: logo, name, and only the flags that matter. */
function HostRow({ c }) {
  return (
    <span className="ec-hostrow">
      {c.logo ? <img src={c.logo} alt="" /> : <span className="ec-hostlogo" aria-hidden="true" />}
      <span className="ec-hostname">
        {c.displayName}
        {c.shortName && c.shortName !== c.displayName && <span className="ec-dim"> · {c.shortName}</span>}
      </span>
      {c.own && <span className="ec-hostflag">UMaps</span>}
      {c.typed && <span className="ec-hostflag warn" title="A name typed before the picker existed — not a club account">typed name</span>}
      {!c.own && !c.typed && c.ready === false && <span className="ec-hostflag warn">needs logo + email</span>}
      {!c.own && !c.typed && c.ready !== false && c.approved === false
        && <span className="ec-hostflag warn" title="Its events stay hidden until the club is verified in /admin → Events Admin">not verified</span>}
      {c.createdBy === 'umaps' && <span className="ec-hostflag" title="Made by UMaps; nobody at the club has signed in yet">
        {c.signedIn ? 'added by UMaps' : 'added by UMaps · not claimed'}</span>}
    </span>
  );
}

/**
 * A new club, made from the Host picker without leaving the event.
 *
 * Everything a club's own profile form asks for except its room — name, short
 * name, logo, socials — plus the address its sign-in link will go to. It is
 * created VERIFIED and marked as made by UMaps; nobody is emailed, now or about
 * any event, until somebody at that address follows a sign-in link the
 * operator sends from /admin (`clubMailable` in api/_accounts.js).
 */
function NewClubDialog({ api, initialName, clubs, onCancel, onCreated }) {
  const [f, setF] = useState({ displayName: initialName || '', shortName: '', email: '', contactEmail: '', logo: '', socials: {} });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const norm = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
  // The server refuses a taken name (`clubNameTaken`); saying so as it is typed
  // points at the club that already exists, which is usually the one wanted.
  const clash = [f.displayName, f.shortName].map(norm).filter(Boolean).map(n =>
    clubs.find(c => norm(c.displayName) === n || norm(c.shortName) === n)).find(Boolean);
  const ok = f.displayName.trim().length >= 2 && isEmail(f.email)
    && (!f.contactEmail.trim() || isEmail(f.contactEmail)) && f.logo && !clash;

  useEffect(() => {
    const esc = (ev) => { if (ev.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [busy, onCancel]);

  const pickLogo = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setErr('That image is over 20 MB — please use a smaller one.'); return; }
    setErr(''); setUploading(true);
    try {
      const dataUrl = await shrinkLogoFile(file);
      if (dataUrl.length * 0.75 > 2 * 1024 * 1024) { setErr('That logo is over 2 MB — please use a smaller image.'); return; }
      const d = await api('', { logo: dataUrl, kind: 'club-logo' });
      set('logo', d.url);
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); }
  };

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const d = await api('', { newClub: {
        displayName: f.displayName.trim(), shortName: f.shortName.trim(),
        email: f.email.trim().toLowerCase(), contactEmail: f.contactEmail.trim(),
        logo: f.logo, socials: f.socials,
      } });
      onCreated(d.club);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  // ⚠️ PORTALLED to the page root. The wizard card is centred with a
  // `transform`, and a transformed ancestor becomes the containing block of
  // anything `position:fixed` inside it — the scrim would cover the card, not
  // the screen. `.ec-root` rather than <body>, because the colour variables
  // are defined there.
  return createPortal(
    <div className="ec-modalscrim" onMouseDown={ev => { if (ev.target === ev.currentTarget && !busy) onCancel(); }}>
      <div className="ec-modal" role="dialog" aria-modal="true" aria-labelledby="ec-newclub-h">
        <h3 id="ec-newclub-h">Add a new club</h3>
        <p className="ec-hint">
          Created verified and marked as added by UMaps. Nobody is emailed — not now,
          and not about any event — until someone at the club signs in with a link
          you send them from /admin → Events Admin.
        </p>
        <div className="ec-row">
          <label className="ec-field">Club name
            <input className="ec-input" autoFocus value={f.displayName}
                   placeholder="Science Students' Association"
                   onChange={e => set('displayName', e.target.value)} />
          </label>
          <label className="ec-field">Short name <span className="ec-opt">optional</span>
            <input className="ec-input" value={f.shortName} maxLength={24} placeholder="SSA"
                   onChange={e => set('shortName', e.target.value)} />
          </label>
        </div>
        {clash && <div className="ec-hint err">“{clash.displayName}” already goes by that name — pick it from the list instead.</div>}
        <div className="ec-row">
          <label className="ec-field">Club email
            <input className="ec-input" type="email" value={f.email} placeholder="club@umanitoba.ca"
                   onChange={e => set('email', e.target.value)} />
            <span className="ec-hint">Where its sign-in link will go. Not contacted until you send one.</span>
          </label>
          <label className="ec-field">Public contact email <span className="ec-opt">optional</span>
            <input className="ec-input" type="email" value={f.contactEmail} placeholder="Same as the club email"
                   onChange={e => set('contactEmail', e.target.value)} />
          </label>
        </div>
        <div className="ec-field">Logo
          <div className="ec-logorow">
            {f.logo ? <img className="ec-logo" src={f.logo} alt="" /> : <div className="ec-logo ec-logo-empty" />}
            <button type="button" className="ec-btn" disabled={uploading || busy}
                    onClick={() => fileRef.current?.click()}>
              {uploading ? 'Uploading…' : f.logo ? 'Change logo' : 'Upload logo'}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden
                   onChange={e => { pickLogo(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
        </div>
        <div className="ec-field">Socials <span className="ec-opt">optional</span>
          <div className="ec-socials">
            {SOCIAL_FIELDS.map(s => (
              <label key={s.key} className="ec-social">
                <span>{s.label}</span>
                <input className="ec-input" value={f.socials[s.key] || ''} placeholder={s.placeholder}
                       onChange={e => setF(p => ({ ...p, socials: { ...p.socials, [s.key]: e.target.value } }))} />
              </label>
            ))}
          </div>
        </div>
        {err && <div className="ec-hint err">{err}</div>}
        <div className="ec-modalactions">
          <button type="button" className="ec-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="ec-btn ec-primary" onClick={create} disabled={!ok || busy || uploading}>
            {busy ? 'Adding…' : 'Add club and host this event'}
          </button>
        </div>
      </div>
    </div>,
    document.querySelector('.ec-root') || document.body,
  );
}

// ── Step 3: the link and the QR ───────────────────────────────────────────────
function StepShare({ e, link, onDownload, onCopied, approved }) {
  const [copied, setCopied] = useState(false);
  const status = statusOf(e);
  const review = reviewNoteOf(e);
  const note = review || STATUS_NOTE[status];
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch { /* select-and-copy still works */ }
    setCopied(true); onCopied?.();
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="ec-share">
      {/* ⚠️ "is live" IS A CLAIM, and it is false for an unverified club and for
          an event UMaps has taken off the map. This is the screen where a club
          copies a link onto a poster, so it is the last moment anybody can be
          told — after this they find out from an attendee standing in the wrong
          building. */}
      <h3 className="ec-sharehead">
        {review?.tone === 'denied' ? `“${e.name}” needs changes`
          : review?.tone === 'pending' ? `“${e.name}” is saved — waiting for review`
          : status === 'disabled' ? `“${e.name}” is turned off`
          : status === 'unlisted' ? `“${e.name}” is unlisted`
            : approved ? `“${e.name}” is live` : `“${e.name}” is saved`}
      </h3>
      {/* Said before the link and the QR, not under them: on this screen the
          next thing somebody does is copy that link onto a poster. */}
      {note && (
        <div className="ec-hold">
          <b>{note.tag}.</b> {note.line}{' '}
          {status === 'disabled' && !review && (
            <>If you think this is a mistake, email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</>
          )}
        </div>
      )}
      {!approved && (
        <div className="ec-hold">
          <b>This link won't work until your club is verified.</b> Anyone opening it now
          won't find the event. Nothing is lost — the moment we verify you, this same
          link starts working and you don't have to change anything you've printed or
          posted. If you've been waiting a few days, email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </div>
      )}
      <p className="ec-hint">
        {/* An off-campus event gets no walking route, so promising one here
            would be the same "is live" mistake in a smaller form. */}
        {isOffCampus(e)
          ? <>Anyone opening this gets the address and a link that opens it in Google Maps.{' '}</>
          : <>Anyone opening this gets directions to {eventWhere(e) || 'the event'} from wherever
              they are standing.{' '}</>}
        Editing the event later keeps the same link.
      </p>
      <div className="ec-sharerow">
        <input className="ec-input" readOnly value={link} onFocus={ev => ev.target.select()} />
        <button className="ec-btn ec-primary" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
      </div>
      <div className="ec-qrrow">
        {/* The mark sits over the code as a DOM <img>, the same way the printed
            building signs do it (BuildingDirectoriesPage / utils/signSheet.js):
            the white disc it sits on is drawn inside the SVG, so it survives a
            print with background graphics turned off. `alt=""` because the code
            beside it already carries the meaning. */}
        <div className="ec-qr">
          {/* ⚠️ ONE WRAPPER AROUND BOTH. The badge is positioned absolutely and
              the CSS says `.ec-qr > div` is its containing block — but the mark
              used to be a SIBLING of the div holding the SVG, not a child of
              it, so `left:50%` and `width:22%` resolved against the nearest
              positioned ancestor further up the page instead. The mark came out
              enormous and centred on the wizard card rather than on the code.
              `dangerouslySetInnerHTML` replaces its element's children, so the
              SVG cannot share a div with the img — hence this wrapper. */}
          <div>
            <div dangerouslySetInnerHTML={{ __html: qrBadgeSvg(link, 190) }} />
            <img className="ec-qr-badge" src={UMAPS_BADGE_URL} alt=""
                 onError={ev => { ev.target.style.display = 'none'; }} />
          </div>
        </div>
        <div>
          <button className="ec-btn" onClick={onDownload}>Download QR code</button>
          <div className="ec-hint">
            Downloads as SVG — vector, so it stays sharp at poster size.
          </div>
          {/* What a scan DOES, since UMaps Mobile 26.11: the app claims this
              link (UMapsMobile src/events/eventLink.ts), opens the event and
              saves it onto the student's Schedules week. Said here because it
              is the reason to put the code on a poster at all — and because the
              link must stay exactly what `eventUrl` builds for that to work. */}
          <div className="ec-appscan">
            <b>Scan to add it to their schedule.</b> Anyone with the UMaps app who
            scans this code gets the event opened in the app and saved to their
            schedule, so it shows up on their week. Without the app it opens the
            event page with directions.
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The satellite map the wizard sits over.
 *
 * Built imperatively: declarative @react-google-maps children are broken in this
 * codebase. It is context rather than a control — the point is to see the campus
 * and, once a room is chosen, to see where on it the event actually is.
 */
/**
 * The campus map the wizard floats over.
 *
 * It draws exactly one thing — whatever `focus` says (see the memo that builds
 * it) — and, on the location step, takes a tap to drop a pin.
 *
 * ⚠️ It is a REAL map, not a picture. Dragging and zooming it is how somebody
 * finds the corner of the concourse they mean, so nothing may sit over it
 * eating gestures; the scrim above is inert unless it is dimming (see its
 * note). `gestureHandling: 'greedy'` is what lets a one-finger drag pan it
 * inside a page that also scrolls.
 */
function CampusMap({ focus, pinning, onPin, onRoom, padTop = 0 }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const shapesRef = useRef([]);
  const labelsRef = useRef([]);
  const markerRef = useRef(null);
  // The room polygons a tap can choose, kept apart from the rest so pinning can
  // switch them off without the plan being rebuilt.
  const pickableRef = useRef([]);
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY || '' });
  // Handlers change every render; the map listener is attached once. A ref
  // keeps the listener pointing at the current one without tearing the map
  // down, which would drop the user's pan and zoom mid-gesture.
  const pinRef = useRef(null);
  pinRef.current = pinning ? onPin : null;
  const roomRef = useRef(null);
  roomRef.current = onRoom;
  // Whether a tap on a room means anything right now: only on the step that
  // asks where the event is, and not while a pin is being placed. A room that
  // lights up under the cursor on a step where clicking it does nothing is a
  // promise the page does not keep.
  const canPick = !!onRoom && !pinning;
  const canPickRef = useRef(false);
  canPickRef.current = canPick;
  // What the map is currently centred on, so a redraw does not re-centre it.
  const lastFocusKey = useRef('');
  // The last frame we fitted, and the card height we fitted it under — so a card
  // that GROWS after the fit can be corrected for. See the padTop effect below.
  const lastFit = useRef(null);
  // Set the moment the user drags the map, and cleared by every fit. After that
  // the view is theirs and nothing here may move it.
  const userMoved = useRef(false);

  /**
   * Put `target` — a whole shape, or a single point — into the strip of map the
   * card is not covering.
   *
   * ⚠️ A POINT NEEDS THE OFFSET AS MUCH AS A SHAPE DOES. `panTo` centres it on
   * the stage, and the top of the stage is the card: measured, a room chosen out
   * of the list landed a few pixels below the card's bottom edge with the plan
   * it belongs to entirely hidden above it. Shifting the centre up by half the
   * card puts the thing in the middle of what can actually be seen.
   */
  const frame = useCallback((map, target, pad) => {
    if (target.position) {
      map.setCenter(target.position);
      map.setZoom(ROOM_ZOOM);
      if (pad > 0) map.panBy(0, -Math.round(pad / 2));
    } else {
      map.fitBounds(target.bounds, { top: Math.round(pad) + 16, bottom: 16, left: 16, right: 16 });
      // Still capped, because fitBounds on a small building lands close enough
      // to lose the neighbours somebody recognises it by.
      if (map.getZoom() > BUILDING_ZOOM) map.setZoom(BUILDING_ZOOM);
    }
    lastFit.current = { ...target, pad };
    userMoved.current = false;
  }, []);

  useEffect(() => {
    if (!isLoaded || !window.google?.maps || !divRef.current || mapRef.current) return;
    const map = new window.google.maps.Map(divRef.current, {
      center: CENTER, zoom: 16, mapTypeId: 'satellite',
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      zoomControl: false, clickableIcons: false, gestureHandling: 'greedy', tilt: 0,
    });
    map.addListener('click', (e) => {
      const fn = pinRef.current;
      if (fn && e.latLng) fn({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });
    // A drag is the user taking the view. `dragstart` fires for a hand on the
    // map and never for panTo/fitBounds, which is exactly the distinction.
    map.addListener('dragstart', () => { userMoved.current = true; });
    // The labels hide below ROOM_LABEL_ZOOM, and an OverlayView only redraws
    // when the projection changes — a zoom that lands on the same centre would
    // otherwise leave them in the wrong state until the next pan.
    map.addListener('idle', () => { labelsRef.current.forEach(l => l.redraw()); });
    mapRef.current = map;
  }, [isLoaded]);

  // The cursor is the only affordance saying a tap will do something.
  //
  // ⚠️ And the room polygons stop taking clicks while pinning: a clickable
  // polygon consumes the tap, so every press over a room would select that room
  // instead of dropping the pin the step is asking for — and a pin's whole
  // purpose is the places that are not rooms, most of which are surrounded by
  // them. Toggled in place rather than by rebuilding the plan.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setOptions({ draggableCursor: pinning ? 'crosshair' : null });
    pickableRef.current.forEach(p => p.setOptions({ clickable: canPick }));
  }, [pinning, canPick]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    shapesRef.current.forEach(s => s.setMap(null));
    shapesRef.current = [];
    pickableRef.current = [];
    labelsRef.current.forEach(l => l.remove());
    labelsRef.current = [];
    if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }

    if (!focus) { map.panTo(CENTER); map.setZoom(16); return; }

    // ── The floor plan, drawn the way /umanitoba draws it ──
    //
    // The building's footprint FIRST, in white: it is the paper the plan is
    // printed on, and the corridors and gaps between wings are that white
    // showing through where no room covers it. Then the rooms in beige on top.
    // A club picking a room should be looking at the same map its attendees
    // will, not a private diagram in different colours.
    // ⚠️ ONE FLOOR'S footprint, and NO accent outline around it. `b.outlines` is
    // every floor's outline in one array, so a six-storey building drew six
    // stacked copies — and the yellow ring the building used to get was drawn
    // six times with it, which is why the edge looked doubled and grew heavier
    // the taller the building. The plan being drawn is what says "this
    // building"; it needs no ring to announce it.
    for (const ring of focus.footprint || []) {
      const st = POLY_COLORS.outline;
      shapesRef.current.push(new window.google.maps.Polygon({
        map, paths: ring, fillColor: st.fill, fillOpacity: st.fillOp,
        strokeColor: st.stroke, strokeWeight: st.strokeW,
        clickable: false, zIndex: 2,
      }));
    }
    const chosen = String(focus.roomNumber ?? '').toLowerCase();
    for (const r of focus.rooms || []) {
      const st = polyStyle(r.type);
      const isChosen = !!chosen && String(r.roomNumber).toLowerCase() === chosen;
      const poly = new window.google.maps.Polygon({
        map, paths: r.ring,
        // The chosen room is filled in the accent, in place: it is one shape
        // among thirty that look alike, so the highlight IS the answer. It is
        // still part of the plan rather than the only thing left of it.
        fillColor: isChosen ? ACCENT : st.fill,
        fillOpacity: isChosen ? 0.5 : st.fillOp,
        strokeColor: isChosen ? ACCENT : st.stroke,
        strokeWeight: isChosen ? 2 : st.strokeW,
        // ⚠️ Not clickable while pinning: a clickable polygon SWALLOWS the tap,
        // and the whole of the pinning step is taps landing on the map. The
        // effect above flips this without rebuilding the plan.
        clickable: !!r.pickable && canPickRef.current,
        zIndex: isChosen ? 5 : 3,
      });
      shapesRef.current.push(poly);
      if (r.pickable) {
        pickableRef.current.push(poly);
        const rest = () => poly.setOptions({
          fillColor: isChosen ? ACCENT : st.fill,
          fillOpacity: isChosen ? 0.5 : st.fillOp,
        });
        // Hover is the same accent at half the strength — "the one under your
        // finger" and "the one you chose" being two intensities of one colour
        // rather than two colours to learn. The cursor is set on the map,
        // because a Polygon has no cursor of its own.
        poly.addListener('mouseover', () => {
          if (!canPickRef.current) return;
          poly.setOptions({ fillColor: ACCENT, fillOpacity: isChosen ? 0.62 : 0.28 });
          map.setOptions({ draggableCursor: 'pointer' });
        });
        poly.addListener('mouseout', () => {
          rest();
          if (!pinRef.current) map.setOptions({ draggableCursor: null });
        });
        poly.addListener('click', () => {
          if (!canPickRef.current) return;
          map.setOptions({ draggableCursor: null });
          roomRef.current?.(r.roomNumber);
        });
      }
      // Only rooms carry a number worth printing — a stairwell's is an internal
      // code, and labelling every shaft would crowd out the rooms.
      if (r.type === 'room' && r.roomNumber) {
        labelsRef.current.push(createRoomLabel(map, ringCentroid(r.ring), String(r.roomNumber)));
      }
    }

    if (focus.position) {
      markerRef.current = new window.google.maps.Marker({ map, position: focus.position });
    }

    // ⚠️ Only re-centre when the SUBJECT changes, and a MOVED PIN IS NOT A NEW
    // SUBJECT. Keying on the position looked right and was exactly wrong: the
    // club taps a spot it can see below the card, the map recentres that spot
    // to the middle of the stage — which is behind the card — and the pin it
    // just placed vanishes. So the key is what is being looked at, not where it
    // is, and a pin dropped or dragged inside the same building leaves the view
    // alone. A room is keyed by its number for the same reason in reverse:
    // picking a different room in the building you are already looking at
    // SHOULD move the map to it.
    const key = `${focus.kind}:${focus.slug}:${focus.kind === 'room' ? focus.roomNumber || '' : ''}`;
    if (key !== lastFocusKey.current) {
      lastFocusKey.current = key;
      if (focus.position) {
        frame(map, { position: focus.position }, padTop);
      } else if (focus.footprint?.length) {
        // The FOOTPRINT is what gets framed — the building no longer has an
        // accent ring to fit to, and the paper the plan is printed on is the
        // right extent anyway.
        const bounds = new window.google.maps.LatLngBounds();
        for (const ring of focus.footprint) for (const pt of ring) bounds.extend(pt);
        // ⚠️ PADDED BY THE CARD'S OWN HEIGHT. The wizard covers the top of the
        // stage, so an unpadded fit centres the building behind it — measured:
        // the Drake Centre landed almost entirely under the card, with a corner
        // of highlight showing below. Padding pushes the whole frame into the
        // strip the reader can actually see, which is also why the zoom comes
        // out sensible: fitting into a shorter box zooms less, not more.
        frame(map, { bounds }, padTop);
      }
    } else if (!focus.position && !focus.footprint?.length) {
      lastFit.current = null;
    }
    // `isLoaded` is a dependency because the map does not exist on the first run
    // — without it a room chosen before the script finished would never be shown.
    // `padTop` is NOT: it changes as the card grows and shrinks, and re-framing
    // the map on every change would fight whoever is dragging it. The effect
    // below handles the one change that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, isLoaded]);

  /**
   * ⚠️ THE CARD GROWS AFTER THE FIT, and the fit has to be redone.
   *
   * Picking a building and the card filling with that building's floors and
   * rooms are one action but two layouts: the fit above runs in the first, with
   * the card's OLD height, and the card is ~180px taller by the second. Measured
   * on a 940px phone stage: EITC E2 was framed under a 400px card and ended up
   * behind a 590px one, with a sliver of yellow outline showing below it — the
   * building you had just chosen, drawn on a plan you could not see.
   *
   * So a fit is redone when the card's height moves by more than a room's worth
   * of list — and never once the user has dragged the map, because from that
   * moment the view is theirs and re-framing it would be the tool taking the map
   * back off somebody who is using it. It works for a chosen room as well as a
   * building: picking one SHRINKS the card (the search list is replaced by a
   * one-line answer), so the room was offset for a card that had already gone.
   */
  useEffect(() => {
    const map = mapRef.current;
    const fit = lastFit.current;
    if (!map || !fit || userMoved.current) return;
    if (Math.abs(padTop - fit.pad) < 40) return;
    frame(map, fit, padTop);
  }, [padTop, frame]);

  if (!MAPS_KEY) return <div className="ec-mapmissing">Map unavailable (no API key configured).</div>;
  return <div className="ec-map" ref={divRef} />;
}

function EventCard({ e, profile, by, link, reach, onOpen, onShare, onDelete }) {
  // The review note wins over the status note: a denied event is also
  // "turned off", but "here is what to change" is the sentence a club can act on.
  const review = reviewNoteOf(e);
  const note = review || STATUS_NOTE[statusOf(e)];
  const tone = review ? review.tone : statusOf(e);
  return (
    <figure className="ec-card2">
      <div className="ec-cardhead">
        {profile?.logo && <img className="ec-cardlogo" src={profile.logo} alt="" />}
        <div className="ec-cardtitle">
          <strong>{e.name || <em>Untitled event</em>}</strong>
          <span className="ec-cardwhen">{fmtWhen(e)}</span>
          {/* The operator's dashboard holds every club's events; which one
              this is matters before anything else on the card. */}
          {by && <span className="ec-cardby">by {by}</span>}
        </div>
      </div>
      {/* ⚠️ The tag sits on the LOCATION line, not up beside the title. In the
          head it competed with the event's name for a card three hundred pixels
          wide and won — "Location Not Set Yet" became "Location No…" — and the
          name is the thing somebody is scanning the grid for. Down here it has
          a short line to itself. Only shown when the status is NOT the ordinary
          one, so it still registers when it appears. */}
      <div className="ec-cardwhere">
        <span>{eventWhere(e) || <span className="ec-dim">No location chosen</span>}</span>
        {note && <span className={`ec-tag ec-tag-${tone}`}>{note.tag}</span>}
      </div>
      {note && <div className="ec-cardnote">{note.line}</div>}
      {/* The club's own tags, drawn the way an attendee will see them. A tag is
          a thing you tick once and then never look at again, so the dashboard
          has to show what is actually set — otherwise "why is our event only
          showing to Engineering" is answered by opening the wizard. */}
      <EventTags event={e} />
      <TicketButton event={e} />
      {/* ⚠️ THE ONE NUMBER ON THIS CARD, and it is OURS. The editable "Attendee
          Signups" box that used to sit above it is gone with the feature — that
          was a figure the CLUB typed about a list we could never see, on a site
          that counts nobody. This one is counted here, which is why it is the
          one that stayed. Its own label is still what says so. */}
      <ReachBox reach={reach} />
      <div className="ec-cardactions">
        <button className="ec-btn" onClick={onOpen}>Edit</button>
        <button className="ec-btn" onClick={onShare}>Link & QR</button>
        <button className="ec-btn" onClick={() => navigator.clipboard?.writeText(link)}>Copy link</button>
        <button className="ec-btn ec-del" onClick={onDelete}>Delete</button>
      </div>
    </figure>
  );
}

/**
 * "Analytics" — one quiet row saying how many people found this event.
 *
 * These figures were a table on /admin, which meant the one group with a reason
 * to act on them — the club that made the event — was the one group that could
 * not see them. A club deciding whether a poster run was worth it, or which of
 * two nights drew people, was guessing.
 *
 * ## One line, and the only one left
 *
 * It arrived as a bordered panel of stat cells with a heading and an
 * explanatory note, which was a lot of furniture for two numbers on a card that
 * already carries a name, a date, a location and tags. It is a label and its
 * values, on one line.
 *
 * ⚠️ IT USED TO BE THE SECOND OF TWO SUCH ROWS, under "Attendee Signups" — a
 * figure the CLUB typed about a list we could not see — and the LABELS were
 * what kept the two apart. That row is gone with the feature, so this is now
 * the only number on the card and the only one that was ever ours: "Viewed"
 * and "Saved" are things that happened on the map and in the app. The verbs
 * stay on the figures anyway. A number on a club's dashboard should say what it
 * is a count of even when nothing is sitting next to it to be confused with.
 *
 * ⚠️ Directions and ticket taps are still counted and still on /admin; they
 * are simply not printed here. Two numbers is what a club reads at a glance,
 * and the third and fourth were the ones nobody could act on.
 *
 * ## Zero and absent are different
 *
 * `reach` missing means the dashboard has not been told anything — an older
 * server, or the counters being down — and the row renders NOTHING, because a
 * label promising numbers with nothing after it is worse than no label. A row
 * of zeroes, which the server sends deliberately for an event nobody has opened
 * yet, is a real answer and prints as "0 Viewed, 0 Saved".
 */
function ReachBox({ reach }) {
  if (!reach) return null;
  const n = (v) => (Number(v) || 0).toLocaleString('en-CA');
  return (
    <div className="ec-row">
      <span className="ec-row-label">Analytics</span>
      <span className="ec-row-value">{n(reach.opens)} Viewed, {n(reach.saves)} Saved</span>
    </div>
  );
}

const CSS = EVENT_TAGS_CSS + `
.ec-root, .ec-login { --bg:#f6f7f9; --panel:#fff; --line:#e2e5ea; --text:#1b1e23; --dim:#6b7280;
  --accent:#4f46e5; --accent-soft:#eef2ff; --field:#fff; --on-accent:#fff;
  color:var(--text); background:var(--bg); min-height:100vh;
  font:15px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
.ec-login { display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:14px; padding:24px; }
.ec-quiet { background:transparent; border-color:transparent; color:var(--dim); }
.ec-quiet:hover { color:var(--text); }
.ec-note { background:var(--accent-soft); color:#3730a3; border-radius:8px; padding:9px 11px;
  font-size:12.5px; line-height:1.45; }
.ec-card { background:var(--panel); border:1px solid var(--line); border-radius:14px; padding:26px;
  width:min(380px,100%); display:flex; flex-direction:column; gap:10px;
  box-shadow:0 6px 30px rgba(0,0,0,.07); }
.ec-card h1 { margin:0; font-size:20px; }
.ec-card p { margin:0 0 6px; color:var(--dim); font-size:13.5px; }
.ec-err { color:#b91c1c; font-size:13px; }

.ec-top { display:flex; align-items:center; gap:12px; padding:12px 18px; background:var(--panel);
  border-bottom:1px solid var(--line); flex-wrap:wrap; position:relative; z-index:30; }
.ec-title { font-size:16px; }
.ec-toplogo { width:26px; height:26px; object-fit:contain; border-radius:6px; }
.ec-who { color:var(--dim); font-size:13px; }
.ec-nav { display:flex; gap:6px; margin-left:8px; }
.ec-tab { background:#eef0f4; border:1px solid transparent; border-radius:999px; padding:6px 14px;
  font:inherit; font-size:13.5px; cursor:pointer; color:var(--text); }
.ec-tab.on { background:var(--accent); color:#fff; }
.ec-status { margin-left:auto; color:var(--dim); font-size:13px; }
.ec-status.err { color:#b91c1c; }

.ec-main { max-width:1040px; margin:0 auto; padding:22px 18px 60px; }
.ec-narrow { max-width:640px; }
.ec-listhead { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:8px; }
.ec-listhead h2 { font-size:19px; margin:0; }
.ec-sec { font-size:12px; text-transform:uppercase; letter-spacing:.06em; color:var(--dim);
  margin:22px 0 10px; }
.ec-empty { color:var(--dim); font-size:14px; }
/* ── The location step ───────────────────────────────────────────────────── */
.ec-loc { display:flex; flex-direction:column; gap:10px; }
.ec-locbar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ec-locwhere { flex:1; min-width:0; display:flex; flex-direction:column; }
.ec-locwhere strong { font-size:15px; }
.ec-locwhere span { font-size:12.5px; color:var(--dim); }
/* The floor tabs. Square-ish keys in a row, the way a lift panel reads, with
   the floor's name spelled out beside them — the pill holds one character and
   "3" alone is only obvious to somebody who already knows what the row is. The
   row scrolls sideways rather than wrapping, so the plan below it never jumps
   down a line as floors are added. */
.ec-floors { display:flex; align-items:center; gap:6px; overflow-x:auto; padding-bottom:2px; }
.ec-floor { flex:none; min-width:34px; height:32px; padding:0 9px; border-radius:9px;
  border:1.5px solid var(--line); background:#fff; color:var(--text);
  font:inherit; font-size:14px; font-weight:700; cursor:pointer; }
.ec-floor:hover:not(.on) { border-color:var(--accent); }
.ec-floor.on { background:var(--accent); border-color:var(--accent); color:var(--on-accent); }
.ec-floorword { font-size:12.5px; color:var(--dim); white-space:nowrap; margin-left:2px; }
/* A scrolling list rather than a dropdown: with no query this is every
   building on campus, and somebody who cannot spell "Buller" still recognises
   it on sight. Capped so the card never grows past the map behind it. */
.ec-blist { list-style:none; margin:0; padding:0; max-height:38vh; overflow-y:auto;
  border:1px solid var(--line); border-radius:10px; }
.ec-blist li + li { border-top:1px solid var(--line); }
.ec-blist button { display:block; width:100%; text-align:left; background:none; border:none;
  padding:10px 12px; font:inherit; font-size:14px; color:var(--text); cursor:pointer; }
.ec-blist button:hover { background:var(--accent-soft); }
.ec-bnone { padding:12px; font-size:13px; color:var(--dim); }
/* Amber, matching the pin the map is about to drop. */
.ec-pinning { background:#fffaf0; border:1px solid #f5d9a8; border-radius:10px; padding:12px 14px;
  display:flex; flex-direction:column; gap:6px; }
.ec-pinning strong { font-size:14px; color:#7a4a00; }
.ec-pinning span { font-size:12.5px; color:#8a6414; line-height:1.5; }
.ec-pinning .ec-btn { align-self:flex-start; }
.ec-pinbtn { align-self:flex-start; }
/* A room number printed on its room. Centred on the polygon, so it is
   translated by half its own size; pointer-events off so it never eats a tap
   meant for the map underneath — this step's whole job is taking taps. The
   white halo is what keeps it readable over a beige room and over the darker
   stair and lift fills without giving every number a plate of its own. */
.ec-roomlabel { position:absolute; transform:translate(-50%,-50%); pointer-events:none;
  font:600 11px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color:#3c3a34; white-space:nowrap; letter-spacing:.01em;
  text-shadow:0 0 3px #fff, 0 0 3px #fff, 0 0 2px #fff; }
/* The short name sits beside the full one and must not claim half the row —
   it holds four characters, the name beside it holds forty. */
.ec-short { flex:0 1 150px; }

.ec-gate { background:#eef2ff; border:1px solid #c7d2fe; color:#3730a3; border-radius:10px;
  padding:12px 14px; font-size:13.5px; margin-bottom:16px; }
/* Amber, not red. Nothing is broken and nothing they made is lost — the club is
   in a queue — and a red banner over a working dashboard reads as an error the
   club has to fix, which is the one thing it cannot do anything about. */
.ec-hold { background:#fffaf0; border:1px solid #f5d9a8; color:#7a4a00; border-radius:10px;
  padding:12px 14px; font-size:13.5px; line-height:1.55; margin-bottom:16px; }
.ec-link { background:none; border:none; padding:0; font:inherit; color:var(--accent);
  text-decoration:underline; cursor:pointer; }

/* The tutorial. A quiet bordered panel rather than a banner: it is an offer,
   not a notice, and it sits above a list the club came here to read. */
.ec-tut { border:1.5px solid var(--line); border-radius:12px; padding:0 14px;
  margin-bottom:18px; background:#fff; }
.ec-tut summary { display:flex; align-items:center; gap:11px; padding:12px 0;
  cursor:pointer; list-style:none; }
.ec-tut summary::-webkit-details-marker { display:none; }
.ec-tut summary b { display:block; font-size:14.5px; }
.ec-tut summary i { display:block; font-style:normal; font-size:12.5px; color:var(--dim);
  margin-top:1px; }
/* A play triangle, sized like an app icon: the one glyph that says "video"
   before anybody has read the words beside it. */
.ec-tutplay { flex:none; width:32px; height:32px; border-radius:9px; display:grid;
  place-items:center; background:var(--accent); color:#fff; font-size:12px;
  padding-left:2px; box-sizing:border-box; }
/* 16:9, so the frame never jumps once the video has decided its own size. */
.ec-tutframe { position:relative; padding-top:56.25%; border-radius:10px;
  overflow:hidden; background:#000; }
.ec-tutframe iframe { position:absolute; inset:0; width:100%; height:100%; border:0; }
.ec-tutlink { display:inline-block; margin:9px 0 13px; font-size:12.5px;
  color:var(--accent); text-decoration:none; }
.ec-tutlink:hover { text-decoration:underline; }
/* Closed, the link would sit under a collapsed summary as a stray line. */
.ec-tut:not([open]) .ec-tutlink { display:none; }

.ec-input { width:100%; box-sizing:border-box; background:#fff; color:var(--text);
  border:1.5px solid var(--line); border-radius:9px; padding:9px 11px; font:inherit; }
.ec-input:focus { outline:none; border-color:var(--accent); }
.ec-btn { background:#fff; color:var(--text); border:1.5px solid var(--line); border-radius:9px;
  padding:8px 14px; font:inherit; font-size:14px; font-weight:600; cursor:pointer; white-space:nowrap; }
.ec-btn:hover:not(:disabled) { border-color:var(--accent); }
.ec-btn:disabled { opacity:.5; cursor:default; }
.ec-primary { background:var(--accent); border-color:var(--accent); color:#fff; }
.ec-del:hover { border-color:#dc2626; color:#dc2626; }

.ec-field { display:block; margin-bottom:14px; font-size:12.5px; color:var(--dim); }
.ec-field .ec-input, .ec-field select { margin-top:5px; }
/* A date field has one width — the date. Stretched across a 700px card it looks
   like a text box that lost its content. */
.ec-date .ec-input { max-width:220px; }

/* ── The time control ─────────────────────────────────────────────────────
   Hour, minutes, and am/pm as two buttons. A native time input's am/pm is
   TYPED — press A or P, or arrow through it — which nobody guesses, and its
   minute field starts empty even though club events start on the hour. */
.ec-time { display:flex; align-items:center; gap:6px; margin-top:5px; flex-wrap:wrap; }
.ec-time .ec-input { margin-top:0; }
.ec-timesel { width:auto; min-width:62px; padding-right:8px; font-size:15px; }
.ec-timesel:disabled { opacity:.55; }
.ec-timecolon { color:var(--dim); font-weight:700; margin:0 -2px; }
/* One segmented control, so the pair reads as two halves of one answer rather
   than as two buttons that might both be pressed. */
.ec-ampm { display:flex; border:1.5px solid var(--line); border-radius:9px; overflow:hidden; }
.ec-ampmbtn { background:#fff; border:none; padding:9px 12px; font:inherit; font-size:13px;
  font-weight:700; color:var(--dim); cursor:pointer; }
.ec-ampmbtn + .ec-ampmbtn { border-left:1.5px solid var(--line); }
.ec-ampmbtn.on { background:var(--accent); color:var(--on-accent); }
.ec-ampmbtn:disabled { opacity:.5; cursor:default; }
.ec-timeclear { background:none; border:none; color:var(--dim); font-size:13px; cursor:pointer;
  padding:6px; line-height:1; }
.ec-timeclear:hover { color:#b91c1c; }
.ec-row { display:flex; gap:12px; flex-wrap:wrap; }
.ec-row .ec-field { flex:1; min-width:140px; }
.ec-hint { display:block; margin-top:5px; font-size:12px; color:var(--dim); }
.ec-hint.err { color:#b91c1c; }
.ec-logorow { display:flex; gap:14px; align-items:center; margin-top:6px; }
.ec-logo { width:74px; height:74px; object-fit:contain; border:1px solid var(--line);
  border-radius:12px; background:#fff; }
.ec-logo-empty { display:flex; align-items:center; justify-content:center; color:var(--dim); font-size:12px; }
.ec-opt { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
  color:var(--dim); background:#eef0f4; border-radius:999px; padding:1px 7px; margin-left:6px; }
.ec-socials { display:grid; gap:8px; grid-template-columns:repeat(auto-fit,minmax(230px,1fr));
  margin-top:6px; }
.ec-social { display:flex; flex-direction:column; gap:3px; font-size:12px; color:var(--dim); }

/* ── Tags (step 1) ──────────────────────────────────────────────────────────
   Five chips in a row, and whatever the ticked ones need underneath. The chips
   wrap rather than scroll: all five have to be readable at once, since these
   are opt-in and an option nobody notices is an option nobody uses. */
.ec-chips { display:flex; flex-wrap:wrap; gap:7px; margin-top:6px; }
.ec-chip { background:#fff; border:1.5px solid var(--line); border-radius:999px;
  padding:7px 14px; font:inherit; font-size:13.5px; font-weight:600; color:var(--text);
  cursor:pointer; }
.ec-chip:hover { border-color:#c8ccd4; }
.ec-chip.on { background:var(--accent); border-color:var(--accent); color:var(--on-accent); }
.ec-chip.small { padding:5px 11px; font-size:12.5px; font-weight:600; }
/* The revealed half. Indented under the chips by a rule rather than by padding,
   so it reads as belonging to the chip above it however many are open. */
.ec-sub { margin:10px 0 2px; padding:10px 0 2px 12px; border-left:3px solid var(--accent-soft);
  display:flex; flex-direction:column; gap:6px; }
.ec-sub > b { font-size:13px; }
.ec-sub > i { font-style:normal; font-size:12px; color:var(--dim); line-height:1.45; }
.ec-sub .ec-field { font-size:12.5px; color:var(--dim); }
/* Capped and scrolling: twenty-one faculties is four rows on a phone and would
   push the wizard's own Next button off the bottom of the card. */
.ec-faclist { display:flex; flex-wrap:wrap; gap:6px; max-height:168px; overflow-y:auto;
  padding:2px; }
.ec-warn { font-size:12px; color:#b45309; font-weight:600; }
/* The custom-tag panel. One row — box, counter, Add — so the counter sits where
   the typing is; under the box it would be read after the fact rather than
   while the fifteenth character is being used up. */
.ec-tagadd { display:flex; align-items:center; gap:7px; margin-top:4px; }
.ec-tagadd .ec-input { flex:1; min-width:0; padding:6px 9px; font-size:13px; }
.ec-tagadd .ec-btn { padding:6px 12px; font-size:13px; }
.ec-tagcount { flex:none; font-size:12px; color:var(--dim); font-variant-numeric:tabular-nums; }
/* Amber at the cap rather than red: nothing is wrong, the club has simply used
   the room it has, and a red number over a box that still works reads as an
   error somebody has to undo. */
.ec-tagcount.at { color:#b45309; font-weight:700; }
/* The tags already added, drawn as the grey pill they will be on the event
   rather than as list rows — what the club is looking at here is the badge it
   is about to publish. The × is the only thing that can be pressed. */
.ec-taglist { display:flex; flex-wrap:wrap; gap:6px; margin-top:2px; }
.ec-taghas { display:inline-flex; align-items:center; gap:6px; background:#f1f3f4;
  color:#3c4043; border:1px solid #dfe1e5; border-radius:999px; padding:3px 6px 3px 10px;
  font-size:12px; font-weight:600; }
.ec-taghas > button { border:none; background:none; cursor:pointer; color:var(--dim);
  font:inherit; font-size:14px; line-height:1; padding:0 4px; }
.ec-taghas > button:hover { color:#c5221f; }
/* .ec-suggest was written for a button; the off-campus "check it opens the
   right place" is an anchor, and inherited the underline on both of its lines.
   (No backticks in here — this stylesheet is a template literal.) */
a.ec-suggest { text-decoration:none; color:inherit; }
a.ec-suggest:hover { text-decoration:none; }
/* The dollar sits inside the field's box, so the number is typed where the
   price will be read. */
.ec-price { display:flex; align-items:center; gap:0; border:1px solid var(--line);
  border-radius:9px; background:var(--field); padding-left:11px; margin-top:4px; }
.ec-price > span { color:var(--dim); font-weight:700; }
.ec-price .ec-input { border:none; background:none; padding-left:4px; }
.ec-price .ec-input:focus { outline:none; }
.ec-price:focus-within { border-color:var(--accent); }

.ec-grid { display:grid; gap:12px; grid-template-columns:repeat(auto-fill, minmax(280px,1fr)); }
.ec-past { opacity:.62; }
.ec-card2 { margin:0; background:var(--panel); border:1px solid var(--line); border-radius:12px;
  padding:14px; display:flex; flex-direction:column; gap:9px; }
.ec-cardhead { display:flex; gap:10px; align-items:center; }
/* Only on the cards that are not in the ordinary state, so it still registers
   when it appears. Grey for unlisted — nothing is wrong, it is simply not being
   advertised — and red for an event whose link has been switched off. */
.ec-tag { margin-left:auto; flex:none; font-size:10.5px; font-weight:800; letter-spacing:.04em;
  text-transform:uppercase; border-radius:999px; padding:3px 9px; }
.ec-tag-unlisted { background:#eceff1; color:#3c4043; }
.ec-tag-disabled { background:#fce8e6; color:#c5221f; }
.ec-tag-pending { background:#fef7e0; color:#b06000; }
.ec-tag-denied { background:#fce8e6; color:#c5221f; }
.ec-cardnote { font-size:12px; line-height:1.5; color:var(--dim); background:#f6f7f9;
  border-radius:9px; padding:8px 10px; }
.ec-cardlogo { width:34px; height:34px; object-fit:contain; border-radius:8px; flex:none; }
.ec-cardtitle { display:flex; flex-direction:column; min-width:0; }
.ec-cardtitle strong { font-size:15px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ec-cardwhen { font-size:12.5px; color:var(--dim); }
.ec-cardwhere { font-size:13px; display:flex; align-items:center; gap:8px; min-width:0; }
.ec-cardwhere > span:first-child { min-width:0; overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap; }
.ec-dim { color:var(--dim); }
.ec-cardactions { display:flex; gap:7px; margin-top:2px; flex-wrap:wrap; }
.ec-cardactions .ec-btn { padding:6px 11px; font-size:13px; }

/* ── The plain row at the foot of a card: Analytics ─────────────────────────
   Deliberately NOT a bordered, tinted panel. A boxed-off block under an event
   that already carries a name, a date, a location, a tag row and a ticket
   button made the card's quietest fact its loudest furniture. A label and a
   value on one line is what this is.

   The label column is still fixed, although there is one row left to line up:
   the shape is what makes it read as a property of the event rather than as a
   widget, and it is what a second row would have to match if one is ever added
   here again. (It used to be two — "Attendee Signups" sat above this, with its
   own input, save button and error slot. The feature is gone, and so are the
   ec-signups rules and the ec-row-note they printed into. No backticks in here:
   this block is a JS template literal, and one would end the string.) */
.ec-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap;
  font-size:13px; color:var(--text); }
.ec-row-label { flex:none; width:118px; color:var(--dim); }
.ec-row-value { font-variant-numeric:tabular-nums; }

/* ── The wizard, over the map ── */
.ec-stage { position:fixed; inset:0; top:53px; }
.ec-map { position:absolute; inset:0; }
.ec-mapmissing { position:absolute; inset:0; display:grid; place-items:center;
  background:#2a2d33; color:#c8ccd4; font-size:14px; }
/* ⚠️ INERT UNLESS IT IS DIMMING. The scrim covers the whole stage, so while it
   took pointer events at every step the map underneath could not be dragged at
   all — and the swallowed drag ended in a click that closed the wizard, losing
   the event being built. A transparent overlay that eats gestures is not a
   scrim, it is a lid. */
.ec-scrim { position:absolute; inset:0; background:transparent; pointer-events:none;
  transition:background .2s ease; }
.ec-scrim.dim { background:rgba(15,18,23,.45); pointer-events:auto; }

.ec-wiz { position:absolute; z-index:10; left:50%; transform:translateX(-50%);
  top:18px; width:min(720px, calc(100% - 28px));
  background:var(--panel); border-radius:18px; box-shadow:0 18px 60px rgba(15,18,23,.4);
  display:flex; flex-direction:column; overflow:hidden;
  animation:ec-pop .22s cubic-bezier(.22,1,.36,1); }
/* Only as tall as it needs to be, capped so a long step still scrolls inside
   rather than running off the bottom — and so the map stays visible under it. */
.ec-wiz.compact { max-height:min(58%, calc(100% - 36px)); }
/* While the room list is open the card is allowed to grow, so ten results are
   visible at once rather than three and a scrollbar. It shrinks back the moment
   a room is picked, because from then on the map is the thing worth seeing. */
.ec-wiz.compact.searching { max-height:calc(100% - 36px); }
.ec-wiz.tall { bottom:18px; }
@keyframes ec-pop { from { opacity:0; transform:translateX(-50%) translateY(10px) scale(.985); }
                    to { opacity:1; transform:translateX(-50%); } }
@media (prefers-reduced-motion: reduce) { .ec-wiz { animation:none; } }

.ec-wizhead { position:relative; padding:14px 46px 12px 18px; border-bottom:1px solid var(--line); flex:none; }
.ec-wizx { position:absolute; top:12px; right:12px; border:none; background:#f1f2f6;
  width:28px; height:28px; border-radius:50%; font-size:13px; cursor:pointer; }
.ec-steps { display:flex; gap:6px; list-style:none; margin:0; padding:0; }
.ec-step { display:flex; align-items:center; gap:7px; flex:1; min-width:0; color:var(--dim);
  font-size:12.5px; }
.ec-stepn { flex:none; width:22px; height:22px; border-radius:50%; display:grid; place-items:center;
  background:#eef0f4; color:var(--dim); font-size:12px; font-weight:800; }
.ec-step.on { color:var(--text); font-weight:700; }
.ec-step.on .ec-stepn { background:var(--accent); color:#fff; }
.ec-step.done .ec-stepn { background:#dcfce7; color:#15803d; }
.ec-steplabel { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.ec-wizbody { flex:1; min-height:0; overflow:auto; padding:18px; }
.ec-wizfoot { flex:none; display:flex; gap:9px; align-items:center; padding:12px 18px;
  border-top:1px solid var(--line); background:#fbfbfc; }
.ec-spacer { flex:1; }

/* The operator's Host picker. The list is IN FLOW, for the reason the room
   search's is (below): .ec-wizbody scrolls, and a floating panel was clipped. */
.ec-hostbtn { display:flex; align-items:center; gap:8px; margin-top:5px; text-align:left;
  cursor:pointer; min-height:42px; }
.ec-hostbtn.empty { border-color:#f5d9a8; background:#fffaf0; }
.ec-hostcaret { margin-left:auto; color:var(--dim); }
.ec-hostpanel { margin-top:6px; padding:8px; background:#fff; border:1px solid var(--line);
  border-radius:11px; box-shadow:0 6px 20px rgba(15,18,23,.1); }
.ec-hostlist { list-style:none; margin:6px 0 0; padding:0; max-height:300px; overflow:auto; }
.ec-hostlist li { padding:7px 8px; border-radius:8px; cursor:pointer; color:var(--text); font-size:14px; }
.ec-hostlist li.hi { background:var(--accent-soft); }
.ec-hostlist li.off { opacity:.55; cursor:not-allowed; }
.ec-hostlist li[aria-selected="true"] { font-weight:700; }
.ec-hostnone { color:var(--dim) !important; cursor:default !important; }
.ec-hostadd { color:var(--accent) !important; font-weight:700; border-top:1px solid var(--line);
  border-radius:0 0 8px 8px !important; margin-top:4px; }
.ec-hostrow { display:flex; align-items:center; gap:8px; min-width:0; flex:1; flex-wrap:wrap; }
.ec-hostrow img, .ec-hostlogo { width:24px; height:24px; border-radius:6px; object-fit:contain;
  flex:none; background:#f1f3f5; }
.ec-hostname { min-width:0; overflow-wrap:anywhere; }
.ec-hostflag { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em;
  padding:2px 6px; border-radius:999px; background:var(--accent-soft); color:var(--accent); white-space:nowrap; }
.ec-hostflag.warn { background:#fffaf0; color:#7a4a00; }
.ec-modalscrim { position:fixed; inset:0; z-index:1000; background:rgba(15,18,23,.45);
  display:flex; align-items:flex-start; justify-content:center; padding:40px 16px; overflow:auto; }
.ec-modal { background:var(--panel); border-radius:14px; padding:20px; width:100%; max-width:620px;
  box-shadow:0 18px 50px rgba(15,18,23,.3); box-sizing:border-box; }
.ec-modal h3 { margin:0 0 4px; font-size:18px; }
.ec-modal > .ec-hint { margin-bottom:14px; }
.ec-modalactions { display:flex; gap:9px; justify-content:flex-end; margin-top:16px; flex-wrap:wrap; }
.ec-cardby { font-size:12.5px; color:var(--dim); margin-top:2px; }

/* Room search.
   The results sit IN FLOW rather than floating. An absolutely-positioned list
   was clipped by .ec-wizbody's own overflow:auto — a scroll container cuts
   off any child that extends past it, however high its z-index — so the list
   was cropped to two or three rows on the compact step. In flow it makes the
   card taller (up to its cap) and the body scrolls to it, which is what an
   overflowing list should do. */
.ec-search { position:relative; }
.ec-results { margin:6px 0 0; padding:5px; list-style:none;
  background:#fff; border:1px solid var(--line); border-radius:11px;
  box-shadow:0 6px 20px rgba(15,18,23,.1);
  /* Ten rows before it starts scrolling — the point of a campus-wide search is
     seeing that "204" exists in a dozen buildings. */
  max-height:400px; overflow:auto; }
/* Inside one building the list is a short scroller, not the screen. The card it
   sits in stays compact so the map behind it — the thing the step is about —
   is still visible and still draggable. */
.ec-results-in { max-height:190px; box-shadow:none; }
.ec-results button { display:flex; align-items:baseline; gap:9px; width:100%; text-align:left;
  background:none; border:none; border-radius:8px; padding:8px 10px; font:inherit; cursor:pointer;
  color:var(--text); }
.ec-results button:hover { background:#f2f2fd; }
.ec-rlabel { font-size:14px; }
/* Only on rows that are somewhere else. It sits next to the room's name rather
   than out at the right margin, because it is part of what the row IS — "405,
   on floor 4" — not a note about it. */
.ec-rfloor { font-size:11px; font-weight:700; letter-spacing:.03em; color:#7a4a00;
  background:#fff3dc; border-radius:999px; padding:1px 8px; white-space:nowrap; }
.ec-rbadge { font-size:12px; color:var(--dim); margin-left:auto; }
/* The "use our club room" shortcut. A dashed outline rather than a filled
   button: it is an offer sitting under an empty field, not the action of the
   step, and a solid button here would compete with "Next". It shows only while
   the box is untouched — once somebody is typing, they are answering the
   question themselves. */
.ec-suggest { display:flex; flex-direction:column; gap:1px; width:100%; text-align:left;
  margin-top:7px; background:#f7f7ff; border:1.5px dashed #c7c9f5; border-radius:10px;
  padding:8px 11px; font:inherit; font-size:13px; font-weight:700; color:var(--accent);
  cursor:pointer; }
.ec-suggest:hover { border-style:solid; }
.ec-suggest span { font-size:12px; font-weight:500; color:var(--dim); }
.ec-noresults { margin-top:6px; background:#fff; border:1px solid var(--line);
  border-radius:11px; padding:10px 12px; font-size:13px; color:var(--dim); }
.ec-chosen { display:flex; align-items:center; gap:12px; margin-top:5px;
  border:1.5px solid var(--accent); border-radius:11px; padding:10px 12px; background:#f7f7ff; }
.ec-chosenmain { display:flex; flex-direction:column; min-width:0; flex:1; }
.ec-chosenmain strong { font-size:14.5px; color:var(--text); }
.ec-chosenmain span { font-size:12.5px; color:var(--dim); }

/* Step 2 — the page, in its own banner */
.ec-page { display:flex; flex-direction:column; gap:16px; }
.ec-pbanner { display:flex; gap:12px; align-items:center; padding-bottom:14px;
  border-bottom:1px solid var(--line); }
.ec-pbanner img { width:46px; height:46px; object-fit:contain; border-radius:10px; flex:none; }
.ec-pbanner div { display:flex; flex-direction:column; min-width:0; }
.ec-pbanner strong { font-size:18px; color:var(--text); }
.ec-pbanner span { font-size:12.5px; color:var(--dim); }
.ec-prule { border:none; border-top:1px solid #d6d9de; margin:0; }
.ec-ptitle { margin:0; font-size:20px; font-weight:800; line-height:1.25; }

/* Step 3 — share */
.ec-share { display:flex; flex-direction:column; gap:12px; }
.ec-sharehead { margin:0; font-size:18px; }
.ec-sharerow { display:flex; gap:8px; }
.ec-qrrow { display:flex; gap:16px; align-items:flex-start; margin-top:4px; }
.ec-appscan { margin-top:12px; font-size:13px; line-height:1.45; color:var(--text);
  background:var(--accent-soft); border-radius:10px; padding:10px 12px; max-width:340px; }
/* The inner wrapper below is the positioning context for the badge, not this
   box: the 8px padding here is part of this one, so centring against it would
   push the mark off the middle of the code by half that. */
.ec-qr { width:170px; flex:none; background:#fff; border:1px solid var(--line);
  border-radius:12px; padding:8px; line-height:0; }
.ec-qr > div { position:relative; }
/* The SVG sits in its own div inside that wrapper, beside the badge. */
.ec-qr svg { width:100%; height:auto; display:block; }
/* The UMaps mark, over the white disc and accent ring the SVG already drew.
   The width is MARK_CSS_WIDTH from utils/qrBadge.js and matches the printed
   signs and the app's share sheet. Height auto, never a fixed height beside a
   percentage width, which renders as a vertical oval. */
.ec-qr-badge { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%);
  width:${MARK_CSS_WIDTH}; height:auto; display:block; }

@media (max-width:560px) {
  .ec-wiz { top:8px; width:calc(100% - 16px); }
  .ec-wiz.tall { bottom:8px; }
  .ec-wiz.compact { max-height:min(72%, calc(100% - 16px)); }
  .ec-steplabel { display:none; }
  .ec-step { flex:none; }
  .ec-qrrow { flex-direction:column; }
}
`;
